import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, apiErr } from "../api";
import { fmtTimeRange } from "../api";
import {
  Users, CreditCard, Sparkles, CalendarClock, Gift, Megaphone,
  TrendingUp, History as HistoryIcon, ImagePlus,
} from "lucide-react";

// Riuso dell'object storage esistente (/api/uploads/image) per la locandina Corsi/Eventi.
const _COURSE_BE = process.env.REACT_APP_BACKEND_URL;
const courseImgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${_COURSE_BE}${u}`);

export function CourseImagePicker({ value, onChange, label }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Usa PNG, JPG o WEBP");
    if (file.size > 3 * 1024 * 1024) return toast.error("Immagine troppo grande (max 3 MB)");
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await api.post("/uploads/image", { data: dataUrl });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="block text-sm text-gray-600 mb-1">{label || "Locandina (dal dispositivo)"}</label>
      <div className="flex items-center gap-3">
        <label className="btn-outline text-sm cursor-pointer inline-flex items-center gap-2" data-testid="course-image-pick">
          <ImagePlus size={16} /> {busy ? "Carico…" : (value ? "Sostituisci immagine" : "Carica immagine")}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="course-image-input" />
        </label>
        {value && <img src={courseImgSrc(value)} alt="locandina" className="w-16 h-16 object-cover rounded-lg border" data-testid="course-image-preview" />}
      </div>
    </div>
  );
}

/* ---------- shared UI ---------- */
export const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
export const resStatusIt = (r) => (r.expired ? "Scaduto"
  : (r.is_current && (r.days_remaining || 0) <= 7) ? "In scadenza"
  : r.is_current ? "Attivo"
  : r.status === "held" ? "In attesa"
  : r.status === "expired" ? "Scaduto"
  : r.status === "active" ? "Attivo" : "Programmato");
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const ST = (t) => (t === "cabina" ? "Cabina" : "Open Space");
export const PTYPE_LABEL = { COWORKING_SINGLE_DAY: "Giornata singola", COWORKING_PACKAGE: "Pacchetto", LATE_RESCHEDULE: "Spostamento tardivo", VETRINA_MESSAGE: "Vetrina", COURSE: "Corso" };
const ptypeLabel = (t) => PTYPE_LABEL[t] || t || "—";
const OPLABEL = { create: "Prenotazione", reschedule: "Spostamento", cancel: "Cancellazione", cancel_late: "Cancellazione tardiva", complete: "Completata", credit: "Riaccredito", ADMIN_CREATE: "Creazione Admin" };
export const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-gray-50 px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-gray-400 leading-tight">{label}</div>
    <div className="text-sm font-semibold text-gray-800 mt-0.5 break-words">{value}</div>
  </div>
);

export const Kpi = ({ label, value, sub, accent }) => (
  <div className="card p-4">
    <div className={`text-2xl font-bold ${accent ? "text-violet-700" : "text-gray-900"}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
    {sub != null && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const Group = ({ title, children }) => (
  <div className="mb-6">
    <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">{title}</div>
    {children}
  </div>
);

export const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] z-[60] overflow-y-auto" data-testid="admin-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] w-full max-w-sm max-h-[85dvh] overflow-y-auto overflow-x-hidden my-auto" onClick={(e) => e.stopPropagation()}>
        {title && <div className="font-semibold mb-3">{title}</div>}
        {children}
      </div>
    </div>,
    document.body
  );
};

/* ---------- period filter ---------- */
export const PeriodFilter = ({ period, setPeriod }) => {
  const presets = [["today", "Oggi"], ["7d", "7 giorni"], ["30d", "30 giorni"], ["month", "Mese"], ["year", "Anno"], ["custom", "Personalizzato"]];
  return (
    <div className="mb-5" data-testid="period-filter">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {presets.map(([k, l]) => (
          <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod({ ...period, preset: k })}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border ${period.preset === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{l}</button>
        ))}
      </div>
      {period.preset === "custom" && (
        <div className="flex gap-2 mt-2 items-center">
          <input type="date" data-testid="period-start" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1 text-xs" />
          <span className="text-xs text-gray-400">→</span>
          <input type="date" data-testid="period-end" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1 text-xs" />
        </div>
      )}
    </div>
  );
};

/* ---------- DASHBOARD ---------- */
const ALERTS = [
  ["artisti_pending", "Artisti da approvare", "coworking", Users],
  ["pagamenti_riconciliare", "Pagamenti da verificare", "pagamenti", CreditCard],
  ["flash_da_approvare", "Flash Day da approvare", "flash", Sparkles],
  ["lead_trico_nuovi", "Lead Trico nuovi", "trico", TrendingUp],
  ["premi_da_consegnare", "Premi Amazon da consegnare", "referral", Gift],
  ["pacchetti_scadenza", "Pacchetti in scadenza (7g)", "coworking", CalendarClock],
  ["vetrina_da_approvare", "Vetrina da approvare", "vetrina", Megaphone],
];

export const DashboardTab = ({ dash, period, setPeriod, goto }) => {
  if (!dash) return <><PeriodFilter period={period} setPeriod={setPeriod} /><p className="text-sm text-gray-400">Caricamento…</p></>;
  const r = dash.revenue, b = dash.bookings, oc = dash.occupazione, pk = dash.pacchetti, ar = dash.artisti, tr = dash.trico, fl = dash.flash, vt = dash.vetrina, rf = dash.referral;
  const anyAlert = ALERTS.some(([k]) => (dash.alerts[k] || 0) > 0);
  return (
    <div data-testid="dashboard-tab">
      <PeriodFilter period={period} setPeriod={setPeriod} />

      <Group title="Da gestire">
        {anyAlert ? (
          <div className="grid grid-cols-2 gap-2">
            {ALERTS.filter(([k]) => (dash.alerts[k] || 0) > 0).map(([k, label, sec, Icon]) => (
              <button key={k} data-testid={`alert-${k}`} onClick={() => goto(sec)}
                className="card p-3 flex items-center justify-between text-left border-l-4 border-l-amber-400">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={16} className="text-amber-500 shrink-0" />
                  <span className="text-xs text-gray-600 truncate">{label}</span>
                </div>
                <span className="text-lg font-bold text-amber-600">{dash.alerts[k]}</span>
              </button>
            ))}
          </div>
        ) : <p className="text-sm text-gray-400">Nessuna azione in sospeso. Tutto sotto controllo.</p>}
      </Group>

      <Group title="Incassi (solo pagamenti confermati)">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Incassi totali MAS" value={fmtEur(r.totali)} accent />
          <Kpi label="Coworking" value={fmtEur(r.coworking)} />
          <Kpi label="Open Space" value={fmtEur(r.open_space)} />
          <Kpi label="Cabina" value={fmtEur(r.cabina)} />
          <Kpi label="Giornate singole" value={fmtEur(r.giornate_singole)} />
          <Kpi label="Pacchetti" value={fmtEur(r.pacchetti)} />
          <Kpi label="Spostamenti tardivi 15 €" value={fmtEur(r.spostamenti)} />
          <Kpi label="Vetrina (prezzo > 0)" value={fmtEur(r.vetrina)} />
          <Kpi label="Resident Flex" value={fmtEur(r.resident_flex)} />
          <Kpi label="Resident Reserved" value={fmtEur(r.resident_reserved)} />
          <Kpi label="Resident Totale" value={fmtEur(r.resident_total)} />
          <Kpi label="Corsi / Eventi" value={fmtEur(r.corsi)} />
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Tricopigmentazione: le conversioni sono mostrate a parte e NON sono conteggiate come fatturato (nessun incasso registrato nel sistema).</p>
      </Group>

      <Group title="Incassi per provenienza">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Stripe (online)" value={fmtEur(r.by_source?.stripe)} data-testid="rev-src-stripe" />
          <Kpi label="Contanti" value={fmtEur(r.by_source?.cash)} data-testid="rev-src-cash" />
          <Kpi label="Bonifico" value={fmtEur(r.by_source?.bank_transfer)} data-testid="rev-src-bank" />
          <Kpi label="Altro" value={fmtEur(r.by_source?.other)} data-testid="rev-src-other" />
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Ripartizione degli stessi incassi confermati per metodo. La somma coincide con gli Incassi totali MAS (nessun doppio conteggio). Non è contabilità fiscale.</p>
      </Group>

      <Group title="Coworking — prenotazioni">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Prenotazioni nel periodo" value={b.periodo} />
          <Kpi label="Future confermate" value={b.future} />
          <Kpi label="Completate" value={b.completate} />
          <Kpi label="Cancellate" value={b.cancellate} />
          <Kpi label="Spostamenti tardivi" value={b.spostamenti} />
        </div>
      </Group>

      <Group title="Occupazione (capacità separata)">
        {oc ? (
          <div className="grid grid-cols-2 gap-3">
            <Kpi label={`Open Space (cap. 2 · ${oc.giorni}g)`} value={`${oc.open_space.rate}%`} sub={`${oc.open_space.occupati}/${oc.open_space.teorici} slot`} accent />
            <Kpi label={`Cabina (cap. 1 · ${oc.giorni}g)`} value={`${oc.cabina.rate}%`} sub={`${oc.cabina.occupati}/${oc.cabina.teorici} slot`} accent />
          </div>
        ) : <p className="text-sm text-gray-400">Seleziona un periodo per calcolare l'occupazione.</p>}
        <p className="text-[11px] text-gray-400 mt-2">Occupazione = slot validi (confirmed/completed) ÷ slot teorici (giorni × capacità). Esclusi cancelled/rifiutati.</p>
      </Group>

      <Group title="Pacchetti & giornate">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Pacchetti venduti" value={pk.venduti} />
          <Kpi label="Pacchetti attivi" value={pk.attivi} />
          <Kpi label="In scadenza (7g)" value={pk.in_scadenza_7} />
          <Kpi label="In scadenza (30g)" value={pk.in_scadenza_30} />
          <Kpi label="Giornate acquistate" value={pk.giornate_acquistate} />
          <Kpi label="Giornate utilizzate" value={pk.giornate_usate} />
          <Kpi label="Giornate residue" value={pk.giornate_residue} />
          <Kpi label="Scaduti con residuo" value={pk.scaduti_residuo} />
        </div>
      </Group>

      <Group title="Artisti">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Registrati" value={ar.registrati} />
          <Kpi label="Approvati" value={ar.approvati} />
          <Kpi label="Attivi nel periodo" value={ar.attivi} sub="con prenotazione valida" />
          <Kpi label="Nuovi nel periodo" value={ar.nuovi} />
        </div>
      </Group>

      <Group title="Tricopigmentazione (conversioni, non fatturato)">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Lead nel periodo" value={tr.totale} />
          <Kpi label="Nuovi" value={tr.nuovo} />
          <Kpi label="Contattati" value={tr.contattato} />
          <Kpi label="Consulenze" value={tr.consulenza} />
          <Kpi label="Confermati" value={tr.confermato} />
          <Kpi label="Trattamenti conclusi" value={tr.concluso} accent />
          <Kpi label="Conv. → confermato" value={`${tr.conv_confermato}%`} />
          <Kpi label="Conv. → concluso" value={`${tr.conv_concluso}%`} />
        </div>
      </Group>

      <Group title="Flash Day">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Creati" value={fl.creati} />
          <Kpi label="Pubblicati" value={fl.pubblicati} />
          <Kpi label="Conclusi" value={fl.conclusi} />
          <Kpi label="Views" value={fl.views} />
          <Kpi label="Lead" value={fl.leads} />
        </div>
      </Group>

      <Group title="Vetrina">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Messaggi nel periodo" value={vt.totale} />
          <Kpi label="A pagamento" value={vt.a_pagamento} />
          <Kpi label="Gratuiti" value={vt.gratuiti} />
        </div>
      </Group>

      <Group title="Inviti / Premi Amazon">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Inviti generati" value={rf.inviti} />
          <Kpi label="Referral identificati" value={rf.identificati} />
          <Kpi label="Conversioni Trico" value={rf.conv_trico} />
          <Kpi label="Conversioni Coworking" value={rf.conv_coworking} />
          <Kpi label="Conversion rate" value={`${rf.conv_rate}%`} />
          <Kpi label="Premi maturati (valore)" value={fmtEur(rf.valore_maturati)} accent />
          <Kpi label="Da consegnare" value={fmtEur(rf.da_consegnare)} />
          <Kpi label="Consegnati" value={fmtEur(rf.valore_consegnati)} />
        </div>
      </Group>

      <Group title="Corsi / Eventi">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Corsi pubblicati" value={dash.corsi?.pubblicati ?? 0} />
          <Kpi label="Iscritti nel periodo" value={dash.corsi?.iscritti ?? 0} />
          <Kpi label="Posti totali" value={dash.corsi?.posti ?? 0} />
          <Kpi label="Incassi corsi" value={fmtEur(r.corsi)} accent />
          <Kpi label="Rimborsi corsi" value={fmtEur(dash.corsi?.rimborsi)} />
        </div>
      </Group>
    </div>
  );
};

/* ---------- COWORKING ---------- */
export const CoworkingTab = ({ data, act }) => {
  const [sub, setSub] = useState("artists");
  const [bkBusy, setBkBusy] = useState(false);
  const [credit, setCredit] = useState(null); // {user_id,name}
  const [cForm, setCForm] = useState({ station_type: "open_space", quantity: 1, reason: "" });
  const [creditBusy, setCreditBusy] = useState(false);
  const [mpay, setMpay] = useState(null); // {user_id,name} pagamento manuale/contanti
  const [mForm, setMForm] = useState({ product_id: "os_1", method: "cash", note: "" });
  const [bkAvail, setBkAvail] = useState(null); // disponibilità reale per la prenotazione Admin
  const [extend, setExtend] = useState(null); // package
  const [extDate, setExtDate] = useState("");
  const [bFilter, setBFilter] = useState("prossime");
  const [detail, setDetail] = useState(null); // scheda artista
  const [newBk, setNewBk] = useState(null); // prenotazione admin per conto artista
  const [bkDetail, setBkDetail] = useState(null); // dettaglio prenotazione (read-only)
  const [pkDetail, setPkDetail] = useState(null); // dettaglio pacchetto (read-only)
  const [artConfirm, setArtConfirm] = useState(null); // {id, action, name} conferma sospendi/rifiuta
  const [cwConfirm, setCwConfirm] = useState(null); // {title, body, label, run} conferme leggere P2
  const bkStatus = (s) => ({ confirmed: "confermata", completed: "completata", cancelled: "annullata" }[s] || s);
  const loadDetail = (aid) => { setDetail({ loading: true }); api.get(`/admin/artists/${aid}/detail`).then((r) => setDetail(r.data)).catch((e) => { toast.error(apiErr(e)); setDetail(null); }); };
  const today = new Date().toISOString().slice(0, 10);

  const [blocks, setBlocks] = useState([]);
  const [blkForm, setBlkForm] = useState({ range: false, date_from: "", date_to: "", scope: "all", note: "" });
  const [blkConfirm, setBlkConfirm] = useState(null); // {existing_bookings, days, payload}
  const [blkBusy, setBlkBusy] = useState(false);
  const scopeLabel = { all: "Tutto lo studio", open_space: "Solo Open Space", cabina: "Solo Cabina Privacy" };
  const loadBlocks = () => api.get("/admin/availability/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  const [capForm, setCapForm] = useState({ date: "", station_type: "open_space", capacity: 2 });
  const [capOverrides, setCapOverrides] = useState([]);
  const loadCapOverrides = () => api.get("/admin/availability/capacity-overrides").then((r) => setCapOverrides(r.data)).catch(() => {});
  useEffect(() => { if (sub === "blocks") { loadBlocks(); loadCapOverrides(); } }, [sub]); // eslint-disable-line

  const submitBlock = async () => {
    if (!blkForm.date_from) return toast.error("Seleziona una data.");
    const payload = { date_from: blkForm.date_from, date_to: blkForm.range ? (blkForm.date_to || blkForm.date_from) : blkForm.date_from, scope: blkForm.scope, note: blkForm.note };
    try {
      const { data: pv } = await api.get(`/admin/availability/preview?date_from=${payload.date_from}&date_to=${payload.date_to}&scope=${payload.scope}`);
      setBlkConfirm({ ...pv, payload });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const confirmBlock = async () => {
    if (!blkConfirm) return;
    setBlkBusy(true);
    try {
      await api.post("/admin/availability/block", blkConfirm.payload);
      toast.success("Disponibilità bloccata.");
      setBlkConfirm(null); setBlkForm({ range: false, date_from: "", date_to: "", scope: "all", note: "" });
      loadBlocks();
    } catch (e) { toast.error(apiErr(e)); } finally { setBlkBusy(false); }
  };
  const reopen = async (b) => {
    try { await api.post("/admin/availability/unblock", { date_from: b.date, date_to: b.date, scope: b.station_type }); toast.success("Disponibilità riaperta."); loadBlocks(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const capDefault = (st) => (st === "open_space" ? 2 : 1);
  const [capLanes, setCapLanes] = useState(null); // {capacity, reserved_guaranteed, public_capacity, occupied_total}
  useEffect(() => {
    if (!capForm.date) { setCapLanes(null); return; }
    api.get(`/availability?date=${capForm.date}&station_type=${capForm.station_type}`)
      .then((r) => setCapLanes(r.data)).catch(() => setCapLanes(null));
  }, [capForm.date, capForm.station_type]);
  const postCapacity = async (st, cap, force) => {
    try {
      await api.post("/admin/availability", { date: capForm.date, station_type: st, capacity: cap, force: !!force });
      toast.success("Capacità aggiornata."); setCapForm({ date: "", station_type: "open_space", capacity: 2 }); setCapLanes(null); loadCapOverrides();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      if (e && e.response && e.response.status === 409 && d && d.require_force) {
        setCwConfirm({ title: "⚠️ Garanzia Reserved a rischio", body: d.message + " Confermi comunque? L'operazione verrà registrata nell'audit.", label: "Forza e registra", run: () => postCapacity(st, cap, true) });
      } else { toast.error(apiErr(e)); }
    }
  };
  const applyCapacity = async () => {
    if (!capForm.date) return toast.error("Seleziona una data.");
    const st = capForm.station_type, cap = Number(capForm.capacity);
    const stLabel = st === "open_space" ? "Open Space" : "Cabina Privacy";
    let existing = 0;
    try {
      const { data: pv } = await api.get(`/admin/availability/preview?date_from=${capForm.date}&date_to=${capForm.date}&scope=${st}`);
      existing = pv.existing_bookings || 0;
    } catch (e) { return toast.error(apiErr(e)); }
    let body;
    if (existing > cap) body = `Sono presenti ${existing} prenotazioni e stai impostando capacità ${cap}. Le prenotazioni esistenti resteranno valide, ma non saranno accettate nuove prenotazioni.`;
    else if (existing > 0 && cap < capDefault(st)) body = `Per questa data risultano già ${existing} prenotazione/i ${stLabel}. Portando la capacità a ${cap}, le prenotazioni esistenti resteranno valide e non saranno accettate ulteriori prenotazioni.`;
    else body = `Imposti la capacità ${stLabel} del ${fmtDate(capForm.date)} a ${cap}.`;
    setCwConfirm({ title: "Confermi la capacità giornaliera?", body, label: "Applica capacità", run: () => postCapacity(st, cap, false) });
  };
  const restoreCapacity = (o) => {
    const def = capDefault(o.station_type);
    setCwConfirm({ title: "Ripristinare la capacità standard?", body: `Ripristinare la capacità del ${fmtDate(o.date)} · ${ST(o.station_type)} a ${def}?`, label: "Ripristina", run: async () => {
      try { await api.post("/admin/availability", { date: o.date, station_type: o.station_type, capacity: def }); toast.success("Capacità ripristinata."); loadCapOverrides(); }
      catch (e) { toast.error(apiErr(e)); }
    }});
  };

  const subs = [["artists", "Artisti"], ["bookings", "Prenotazioni"], ["packages", "Pacchetti"], ["blocks", "Disponibilità"]];
  const bFiltered = (data.bookings || []).filter((b) => {
    if (bFilter === "oggi") return b.date === today;
    if (bFilter === "prossime") return b.status === "confirmed" && b.date >= today;
    if (bFilter === "completate") return b.status === "completed";
    if (bFilter === "cancellate") return b.status === "cancelled";
    if (bFilter === "open_space") return b.station_type === "open_space";
    if (bFilter === "cabina") return b.station_type === "cabina";
    return true;
  });

  return (
    <div data-testid="coworking-tab">
      <div className="flex gap-2 mb-4">
        {subs.map(([k, l]) => (
          <button key={k} data-testid={`cw-sub-${k}`} onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${sub === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{l}</button>
        ))}
      </div>

      {sub === "artists" && (
        <div className="space-y-3">
          {(data.artists || []).map((a) => (
            <div key={a.id} className="card p-4" data-testid={`artist-${a.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 cursor-pointer" onClick={() => loadDetail(a.id)} data-testid={`artist-open-${a.id}`}><div className="font-semibold truncate">{a.name}</div><div className="text-xs text-gray-500 truncate">{a.profession} · {a.email}</div></div>
                <span className={`badge-chip shrink-0 ${a.approved ? "state-DISPONIBILE" : "state-ULTIMA"}`}>{a.status || "pending"}</span>
              </div>
              <button data-testid={`artist-detail-${a.id}`} onClick={() => loadDetail(a.id)} className="btn-primary w-full text-sm py-2.5 px-4 mt-3">Apri Scheda artista</button>
              <div className="flex gap-2 mt-2 flex-wrap">
                {!a.approved && <button data-testid={`approve-${a.id}`} onClick={() => setCwConfirm({ title: "Attivare l'artista?", body: `Attivare l'artista ${a.name}?`, label: "Attiva artista", run: () => act(() => api.post(`/admin/artists/${a.id}/approve`)) })} className="btn-outline text-xs py-2 px-3">Approva</button>}
                {a.status === "suspended"
                  ? <button data-testid={`reactivate-${a.id}`} onClick={() => setCwConfirm({ title: "Riattivare l'artista?", body: `Riattivare l'artista ${a.name}?`, label: "Riattiva artista", run: () => act(() => api.post(`/admin/artists/${a.id}/approve`)) })} className="btn-outline text-xs py-2 px-3">Riattiva</button>
                  : <button data-testid={`suspend-${a.id}`} onClick={() => setArtConfirm({ id: a.id, action: "suspend", name: a.name })} className="btn-outline text-xs py-2 px-3">Sospendi</button>}
                {!a.approved && <button data-testid={`reject-${a.id}`} onClick={() => setArtConfirm({ id: a.id, action: "reject", name: a.name })} className="btn-outline text-xs py-2 px-3">Rifiuta</button>}
                <button data-testid={`credit-${a.id}`} onClick={() => { setCredit({ user_id: a.id, name: a.name }); setCForm({ station_type: "open_space", quantity: 1, reason: "" }); }} className="btn-outline text-xs py-2 px-3">Riaccredito</button>
                <button data-testid={`manual-pay-${a.id}`} onClick={() => { setMpay({ user_id: a.id, name: a.name }); setMForm({ product_id: "os_1", method: "cash", note: "" }); }} className="btn-outline text-xs py-2 px-3">Pagamento manuale</button>
              </div>
            </div>
          ))}
          {(data.artists || []).length === 0 && <p className="text-sm text-gray-400">Nessun artista.</p>}
        </div>
      )}

      {sub === "bookings" && (
        <div>
          <button data-testid="admin-book-new" onClick={() => setNewBk({ user_id: "", date: "", station_type: "open_space" })} className="btn-primary text-sm w-full mb-3">+ Prenota per artista</button>
          <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
            {[["oggi", "Oggi"], ["prossime", "Prossime"], ["completate", "Completate"], ["cancellate", "Cancellate"], ["open_space", "Open Space"], ["cabina", "Cabina"]].map(([k, l]) => (
              <button key={k} data-testid={`bk-filter-${k}`} onClick={() => setBFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${bFilter === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-2">
            {bFiltered.map((b) => (
              <div key={b.id} onClick={() => setBkDetail(b)} className="card p-3 flex justify-between items-center gap-2 cursor-pointer" data-testid={`booking-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{b.user_name}{b.source === "admin" ? " · da Admin" : ""}</div>
                  <div className="text-xs text-gray-500">{fmtDate(b.date)} · {ST(b.station_type)} · {bkStatus(b.status)}{b.rescheduled_at ? " · spostata" : ""}</div>
                </div>
                {!b.completed && b.status === "confirmed" && <button data-testid={`complete-${b.id}`} onClick={(e) => { e.stopPropagation(); setCwConfirm({ title: "Completare la giornata?", body: `Segnare come completata la prenotazione${b.artist_name ? " di " + b.artist_name : ""} del ${fmtDate(b.date)} · ${b.station_type === "open_space" ? "Open Space" : "Cabina"}?`, label: "Conferma", run: () => act(() => api.post(`/admin/bookings/${b.id}/complete`)) }); }} className="btn-primary text-xs py-1.5 px-3 shrink-0">Completa</button>}
                {b.completed && <span className="text-xs text-green-600 shrink-0">✓ completata</span>}
              </div>
            ))}
            {bFiltered.length === 0 && <p className="text-sm text-gray-400">Nessuna prenotazione in questa vista.</p>}
          </div>
        </div>
      )}

      {sub === "packages" && (
        <div className="space-y-2">
          {(data.packages || []).map((p) => (
            <div key={p.id} onClick={() => setPkDetail(p)} className="card p-3 cursor-pointer" data-testid={`pkg-${p.id}`}>
              <div className="font-medium text-sm">{p.label || "Pacchetto (record incompleto)"}</div>
              <div className="text-xs text-gray-500">{p.user_name} · {p.user_email}</div>
              {p.incomplete ? (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mt-1" data-testid={`pkg-incomplete-${p.id}`}>Record legacy incompleto — non prorogabile</div>
              ) : (
                <>
                  <div className="text-xs text-gray-500">Residue: {p.remaining} · Acquisto: {fmtDate(p.purchase_date)} · Scadenza: {fmtDate(p.expiry_date)}</div>
                  {p.extensions?.length > 0 && <div className="text-[11px] text-violet-700 mt-1">Prorogato {p.extensions.length}x</div>}
                  <button data-testid={`extend-${p.id}`} onClick={(e) => { e.stopPropagation(); setExtend(p); setExtDate((p.expiry_date || "").slice(0, 10)); }} className="btn-outline text-xs py-1.5 px-3 mt-2">Proroga scadenza</button>
                </>
              )}
            </div>
          ))}
          {(data.packages || []).length === 0 && <p className="text-sm text-gray-400">Nessun pacchetto multi-giornata.</p>}
        </div>
      )}

      {sub === "blocks" && (
        <div className="space-y-4" data-testid="cw-blocks">
          <div className="card p-4">
            <div className="font-semibold text-sm mb-3">Blocca disponibilità</div>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input data-testid="blk-range" type="checkbox" checked={blkForm.range} onChange={(e) => setBlkForm({ ...blkForm, range: e.target.checked })} />
              Intervallo di date (Da → A)
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-xs text-gray-500">{blkForm.range ? "Da" : "Data"}
                <input data-testid="blk-from" type="date" min={today} value={blkForm.date_from} onChange={(e) => setBlkForm({ ...blkForm, date_from: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" />
              </label>
              {blkForm.range && (
                <label className="text-xs text-gray-500">A
                  <input data-testid="blk-to" type="date" min={blkForm.date_from || today} value={blkForm.date_to} onChange={(e) => setBlkForm({ ...blkForm, date_to: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" />
                </label>
              )}
            </div>
            <label className="block text-xs text-gray-500 mb-2">Ambito
              <select data-testid="blk-scope" value={blkForm.scope} onChange={(e) => setBlkForm({ ...blkForm, scope: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm">
                <option value="all">Tutto lo studio</option>
                <option value="open_space">Solo Open Space</option>
                <option value="cabina">Solo Cabina Privacy</option>
              </select>
            </label>
            <label className="block text-xs text-gray-500 mb-3">Nota interna (facoltativa)
              <input data-testid="blk-note" type="text" value={blkForm.note} placeholder="Es. Chiusura, Ferie, Manutenzione, Evento MAS" onChange={(e) => setBlkForm({ ...blkForm, note: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" />
            </label>
            <button data-testid="blk-submit" onClick={submitBlock} className="btn-primary w-full text-sm">Blocca disponibilità</button>
          </div>

          <div className="card p-4" data-testid="cw-capacity">
            <div className="font-semibold text-sm mb-3">Capacità giornaliera</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="text-xs text-gray-500">Data
                <input data-testid="cap-date" type="date" min={today} value={capForm.date} onChange={(e) => setCapForm({ ...capForm, date: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-base" />
              </label>
              <label className="text-xs text-gray-500">Postazione
                <select data-testid="cap-station" value={capForm.station_type} onChange={(e) => { const st = e.target.value; setCapForm({ ...capForm, station_type: st, capacity: st === "open_space" ? 2 : 1 }); }} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-base">
                  <option value="open_space">Open Space</option>
                  <option value="cabina">Cabina Privacy</option>
                </select>
              </label>
            </div>
            <label className="block text-xs text-gray-500 mb-3">Capacità
              <select data-testid="cap-value" value={capForm.capacity} onChange={(e) => setCapForm({ ...capForm, capacity: Number(e.target.value) })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-base">
                {capForm.station_type === "open_space"
                  ? [<option key={2} value={2}>2 — Tutte disponibili</option>, <option key={1} value={1}>1 — Una postazione disponibile</option>, <option key={0} value={0}>0 — Nessuna disponibile</option>]
                  : [<option key={1} value={1}>1 — Disponibile</option>, <option key={0} value={0}>0 — Non disponibile</option>]}
              </select>
            </label>
            <button data-testid="cap-submit" onClick={applyCapacity} className="btn-primary w-full text-sm">Imposta capacità</button>
            {capLanes && (
              <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs" data-testid="cap-lanes">
                <div className="font-semibold text-gray-700 mb-1">Ripartizione capacità del {fmtDate(capForm.date)}</div>
                <div className="flex justify-between"><span className="text-gray-500">Capacità totale</span><b data-testid="cap-lane-total">{capLanes.capacity}</b></div>
                <div className="flex justify-between"><span className="text-gray-500">Quota Reserved garantita</span><b data-testid="cap-lane-reserved">{capLanes.reserved_guaranteed}</b></div>
                <div className="flex justify-between"><span className="text-gray-500">Pubblicamente prenotabili</span><b data-testid="cap-lane-public">{capLanes.public_capacity}</b></div>
                <div className="flex justify-between"><span className="text-gray-500">Occupazione reale</span><b data-testid="cap-lane-occupied">{capLanes.occupied_total}</b></div>
              </div>
            )}
          </div>

          <div>
            <div className="font-semibold text-sm mb-2">Blocchi attivi</div>
            <div className="space-y-2">
              {blocks.map((b) => (
                <div key={b.id} className="card p-3 flex justify-between items-center gap-2" data-testid={`block-${b.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{b.date} · {ST(b.station_type)}</div>
                    {b.note && <div className="text-xs text-gray-500 truncate">{b.note}</div>}
                  </div>
                  <button data-testid={`reopen-${b.id}`} onClick={() => setCwConfirm({ title: "Riaprire la disponibilità?", body: `Riaprire la disponibilità del ${fmtDate(b.date)}${b.station_type ? " · " + (b.station_type === "open_space" ? "Open Space" : b.station_type === "cabina" ? "Cabina" : "Tutto lo studio") : ""}?`, label: "Riapri", run: () => reopen(b) })} className="btn-outline text-xs py-1.5 px-3 shrink-0">Riapri disponibilità</button>
                </div>
              ))}
              {blocks.length === 0 && <p className="text-sm text-gray-400">Nessun blocco attivo.</p>}
            </div>
          </div>

          <div data-testid="cw-capacity-overrides">
            <div className="font-semibold text-sm mb-2">Capacità personalizzate</div>
            <div className="space-y-2">
              {capOverrides.map((o) => (
                <div key={o.id} className="card p-3 flex justify-between items-center gap-2" data-testid={`capov-${o.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{fmtDate(o.date)} · {ST(o.station_type)}</div>
                    <div className="text-xs text-gray-500">Capacità impostata: {o.capacity} / {o.station_type === "open_space" ? 2 : 1}</div>
                  </div>
                  <button data-testid={`caprestore-${o.id}`} onClick={() => restoreCapacity(o)} className="btn-outline text-xs py-1.5 px-3 shrink-0">Ripristina capacità standard</button>
                </div>
              ))}
              {capOverrides.length === 0 && <p className="text-sm text-gray-400">Nessuna capacità personalizzata.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Riaccredito manuale */}
      <Modal open={!!bkDetail} onClose={() => setBkDetail(null)} title="Dettaglio prenotazione">
        {bkDetail && <div className="space-y-1 text-xs" data-testid="booking-detail">
          <div><b>Artista:</b> {bkDetail.user_name}</div>
          <div><b>Data:</b> {fmtDate(bkDetail.date)}</div>
          <div><b>Postazione:</b> {ST(bkDetail.station_type)}</div>
          <div><b>Stato:</b> {bkStatus(bkDetail.status)}</div>
          <div><b>Origine:</b> {bkDetail.source === "admin" ? "Inserita da Admin" : "Artista"}</div>
          {bkDetail.used_balance != null && <div><b>Giornata da pacchetto:</b> {bkDetail.used_balance ? "Sì" : "No"}</div>}
          {bkDetail.consumed_purchase_id && <div><b>Pacchetto collegato:</b> {bkDetail.consumed_purchase_id}</div>}
          {bkDetail.rescheduled_at && <div><b>Spostata il:</b> {fmtDate(bkDetail.rescheduled_at)}</div>}
          {bkDetail.created_at && <div><b>Creata il:</b> {fmtDate(bkDetail.created_at)}</div>}
          <button onClick={() => setBkDetail(null)} className="btn-outline w-full text-sm mt-3">Chiudi</button>
        </div>}
      </Modal>

      <Modal open={!!pkDetail} onClose={() => setPkDetail(null)} title="Dettaglio pacchetto">
        {pkDetail && <div className="space-y-1 text-xs" data-testid="package-detail">
          <div><b>Artista:</b> {pkDetail.user_name} {pkDetail.user_email ? `· ${pkDetail.user_email}` : ""}</div>
          <div><b>Tipo:</b> {pkDetail.label || "Record incompleto"}</div>
          {pkDetail.type && <div><b>Postazione:</b> {ST(pkDetail.type)}</div>}
          {pkDetail.days != null && <div><b>Giornate originarie:</b> {pkDetail.days}</div>}
          {pkDetail.remaining != null && <div><b>Residue:</b> {pkDetail.remaining}</div>}
          {pkDetail.purchase_date && <div><b>Acquisto:</b> {fmtDate(pkDetail.purchase_date)}</div>}
          {pkDetail.expiry_date && <div><b>Scadenza:</b> {fmtDate(pkDetail.expiry_date)}</div>}
          {pkDetail.amount != null && <div><b>Importo:</b> {fmtEur(pkDetail.amount)}</div>}
          {pkDetail.extensions?.length > 0 && <div><b>Proroghe:</b> {pkDetail.extensions.length}</div>}
          {pkDetail.incomplete && <div className="text-amber-700">Record legacy incompleto — dati parziali non ricostruiti.</div>}
          <button onClick={() => setPkDetail(null)} className="btn-outline w-full text-sm mt-3">Chiudi</button>
        </div>}
      </Modal>

      <Modal open={!!newBk} onClose={() => { setNewBk(null); setBkAvail(null); }} title="Prenota per artista">
        {newBk && (() => {
          const loadAvail = (date, station) => {
            if (!date || !station) { setBkAvail(null); return; }
            setBkAvail({ loading: true });
            api.get(`/availability?date=${date}&station_type=${station}`).then((r) => setBkAvail(r.data)).catch(() => setBkAvail({ error: true }));
          };
          // Anteprima diritto (indicativa; il server resta autoritativo su FIFO/priorità).
          const st = newBk.station_type;
          const resv = (newBk.resident || []).find((r) => r.type === "RESIDENT_RESERVED" && r.is_current);
          const flex = (newBk.resident || []).find((r) => r.type === "RESIDENT_FLEX" && r.is_current && (r.credits_remaining ?? 0) > 0);
          const bal = (newBk.balance || {})[st] ?? 0;
          let hint, canBook;
          if (st === "open_space" && resv) { hint = "Prenotazione tramite Resident Reserved (Open Space garantita)."; canBook = true; }
          else if (flex) { hint = "Prenotazione tramite Resident Flex."; canBook = true; }
          else if (bal > 0) { hint = `Verrà utilizzata 1 giornata ${st === "open_space" ? "Open Space" : "Cabina Privacy"}.`; canBook = true; }
          else { hint = "L'artista non dispone di giornate/diritti utilizzabili per questa prenotazione."; canBook = false; }
          const availTxt = bkAvail?.loading ? "Verifica disponibilità…" : bkAvail?.error ? "Disponibilità non verificabile." : bkAvail ? `${bkAvail.state} · ${bkAvail.remaining} post. disponibili` : "";
          const full = bkAvail && !bkAvail.loading && !bkAvail.error && bkAvail.remaining <= 0 && !(st === "open_space" && resv);
          return (
          <div className="space-y-3" data-testid="admin-book-form">
            {newBk.artist_name ? (
              <div className="text-sm" data-testid="admin-book-artist-name"><span className="text-gray-500">Artista:</span> <b>{newBk.artist_name}</b></div>
            ) : (
              <label className="block text-sm">Artista
                <select data-testid="admin-book-artist" className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1" value={newBk.user_id} onChange={(e) => setNewBk({ ...newBk, user_id: e.target.value })}>
                  <option value="">Seleziona…</option>
                  {(data.artists || []).filter((a) => a.approved).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
            )}
            <label className="block text-sm">Data
              <input data-testid="admin-book-date" type="date" className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1" value={newBk.date} onChange={(e) => { setNewBk({ ...newBk, date: e.target.value }); loadAvail(e.target.value, newBk.station_type); }} />
            </label>
            <label className="block text-sm">Postazione
              <select data-testid="admin-book-station" className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1" value={newBk.station_type} onChange={(e) => { setNewBk({ ...newBk, station_type: e.target.value }); loadAvail(newBk.date, e.target.value); }}>
                <option value="open_space">Open Space</option>
                <option value="cabina">Cabina Privacy</option>
              </select>
            </label>
            {availTxt && <div data-testid="admin-book-availability" className={`text-xs ${full ? "text-rose-600" : "text-gray-500"}`}>{availTxt}</div>}
            <div data-testid="admin-book-entitlement" className={`text-xs rounded-lg px-3 py-2 ${canBook ? "bg-violet-50 text-violet-700" : "bg-rose-50 text-rose-600"}`}>{hint}</div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setNewBk(null); setBkAvail(null); }} className="btn-outline flex-1 text-sm">Annulla</button>
              <button data-testid="admin-book-submit" disabled={!newBk.user_id || !newBk.date || !canBook || full || bkBusy} onClick={async () => {
                if (bkBusy || !newBk.user_id || !newBk.date) return;
                setBkBusy(true);
                try {
                  await api.post("/admin/bookings/create", { user_id: newBk.user_id, date: newBk.date, station_type: newBk.station_type });
                  setNewBk(null); setBkAvail(null);
                  await act(() => Promise.resolve(), "Prenotazione creata correttamente");
                } catch (e) { toast.error(apiErr(e)); }
                finally { setBkBusy(false); }
              }} className="btn-primary flex-1 text-sm">{bkBusy ? "Prenotazione…" : "Conferma prenotazione"}</button>
            </div>
          </div>
          );
        })()}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Scheda artista">
        {detail && detail.loading && <p className="text-sm text-gray-400">Caricamento…</p>}
        {detail && !detail.loading && <div className="space-y-4 max-h-[75vh] overflow-y-auto overflow-x-hidden" data-testid="artist-detail">
          {/* Anagrafica */}
          <div data-testid="artist-anagrafica">
            <div className="font-semibold text-base break-words">{detail.profile?.name}{detail.profile?.surname ? ` ${detail.profile.surname}` : ""}</div>
            <div className="text-sm text-gray-500 break-words">{detail.profile?.email} · {detail.profile?.phone || "—"}</div>
            <div className="text-xs text-gray-500 break-words">{detail.profile?.profession || detail.profile?.technique || "—"}{detail.profile?.artist_name ? ` · ${detail.profile.artist_name}` : ""}{detail.profile?.city ? ` · ${detail.profile.city}` : ""}</div>
            <div className="text-xs text-gray-500">Stato: {detail.profile?.status || (detail.profile?.approved ? "active" : "pending")} · Registrato: {fmtDate(detail.profile?.created_at)}</div>
          </div>

          {/* KPI */}
          {detail.kpi && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="artist-kpi">
            <Stat label="Giornate acquistate" value={detail.kpi.giornate_acquistate} testid="artist-kpi-acquistate" />
            <Stat label="Accreditate da Admin" value={(detail.credit_adjustments || []).reduce((s, c) => s + (Number(c.quantity) > 0 ? Number(c.quantity) : 0), 0)} testid="artist-kpi-admin-credited" />
            <Stat label="Saldo disponibile" value={detail.kpi.giornate_residue} testid="artist-kpi-residue" />
            <Stat label="Completate" value={detail.kpi.giornate_completate} />
            <Stat label="Future" value={detail.kpi.giornate_future} />
            <Stat label="Totale pagato" value={fmtEur(detail.kpi.totale_pagato)} />
            <Stat label="Rimborsato" value={fmtEur(detail.kpi.totale_rimborsato)} />
            <Stat label="Netto" value={fmtEur(detail.kpi.netto)} testid="artist-economic-total" />
            <Stat label="Ultima attività" value={detail.kpi.ultima_attivita ? fmtDate(detail.kpi.ultima_attivita) : "—"} />
          </div>}

          {/* P4 — Prenota per artista (artista pre-selezionato dalla scheda) */}
          {detail.profile?.approved && detail.profile?.status !== "suspended" && (
            <button data-testid="artist-book-for" onClick={() => {
              const p = detail.profile;
              setDetail(null); setBkAvail(null);
              setNewBk({ user_id: p.id, artist_name: `${p.name || ""}${p.surname ? " " + p.surname : ""}`.trim(),
                date: "", station_type: "open_space", balance: detail.balance, resident: detail.resident || [] });
            }} className="btn-primary w-full text-sm py-2.5">Prenota per artista</button>
          )}

          {/* Situazione Coworking */}
          <div data-testid="artist-coworking">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Situazione Coworking</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Stat label="Crediti Open Space" value={detail.balance?.open_space ?? 0} />
              <Stat label="Crediti Cabina" value={detail.balance?.cabina ?? 0} />
            </div>
            {(detail.packages || []).map((p) => (
              <div key={p.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                <span className="min-w-0 break-words">{p.label || `${p.days} giornate`} · {ST(p.type)} · acq. {fmtDate(p.purchase_date)}</span>
                <span className="text-gray-500 shrink-0">resid. {p.remaining ?? "—"}{p.expiry_date ? ` · scad. ${fmtDate(p.expiry_date)}` : ""}</span>
              </div>
            ))}
            {(detail.packages || []).length === 0 && <div className="text-xs text-gray-400">Nessun pacchetto.</div>}
            {(detail.resident || []).length > 0 && (
              <div className="mt-2" data-testid="artist-resident">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 mb-1">Resident</div>
                {(detail.resident || []).map((r) => (
                  r.type === "RESIDENT_RESERVED" ? (
                    <div key={r.id} className="text-xs border-b border-gray-50 py-1" data-testid={`artist-reserved-${r.id}`}>
                      <div className="flex justify-between gap-2">
                        <span className="min-w-0 break-words font-medium">Reserved · Open Space garantita</span>
                        <span className="text-gray-500 shrink-0">{resStatusIt(r)}{r.is_current ? ` · ${r.days_remaining} gg` : ""}</span>
                      </div>
                      <div className="text-gray-500">{fmtDate(r.start_date)}→{fmtDate(r.last_usable_date)} · pagato {fmtEur(r.price)} · prenotati {r.booked_days ?? 0} gg{r.renewed_from ? " · rinnovo" : ""}</div>
                      {r.guarantee_anomaly && <div className="text-rose-600" data-testid="reserved-anomaly">⚠ Anomalia garanzia P5.3</div>}
                    </div>
                  ) : (
                    <div key={r.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                      <span className="min-w-0 break-words">Flex · {fmtDate(r.start_date)}→{fmtDate(r.last_usable_date)} · {resStatusIt(r)}</span>
                      <span className="text-gray-500 shrink-0">{r.credits_used}/{r.credits_total} · resid. {r.credits_remaining}</span>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Prenotazioni (Future / Passate / Annullate) */}
          <div data-testid="artist-bookings">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Prenotazioni ({detail.bookings?.length || 0})</div>
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const bk = detail.bookings || [];
              const future = bk.filter((b) => b.status === "confirmed" && !b.completed && (b.date || "") >= today);
              const past = bk.filter((b) => b.status !== "cancelled" && (b.completed || (b.date || "") < today));
              const cancelled = bk.filter((b) => b.status === "cancelled");
              const row = (b) => (
                <div key={b.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                  <span className="min-w-0 break-words">{fmtDate(b.date)} · {ST(b.station_type)}</span>
                  <span className="text-gray-500 shrink-0">{b.completed ? "completata" : bkStatus(b.status)}{b.source === "admin" ? " · Admin" : ""}</span>
                </div>
              );
              const grp = (title, arr, tid) => (
                <div className="mt-1" data-testid={tid}>
                  <div className="text-[11px] text-gray-500 mb-0.5">{title} ({arr.length})</div>
                  {arr.length ? arr.slice(0, 15).map(row) : <div className="text-xs text-gray-400">Nessuna.</div>}
                </div>
              );
              if (!bk.length) return <div className="text-xs text-gray-400">Nessuna prenotazione.</div>;
              return <>{grp("Future", future, "artist-bookings-future")}{grp("Passate", past, "artist-bookings-past")}{grp("Annullate", cancelled, "artist-bookings-cancelled")}</>;
            })()}
            {(detail.booking_history || []).length > 0 && <>
              <div className="text-[11px] text-gray-500 mt-2 mb-1" data-testid="artist-booking-history">Movimenti ({detail.booking_history.length})</div>
              {detail.booking_history.slice(0, 20).map((h) => (
                <div key={h.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                  <span className="min-w-0 break-words">{fmtDateTime(h.timestamp)} · {OPLABEL[h.operation] || h.operation}{h.old_date ? ` · ${fmtDate(h.old_date)}` : ""}{h.new_date ? ` → ${fmtDate(h.new_date)}` : ""}</span>
                  <span className="text-gray-500 shrink-0">{h.source === "admin" ? "Admin" : "Artista"}{h.amount ? ` · ${fmtEur(h.amount)}` : ""}</span>
                </div>
              ))}
            </>}
            {(detail.flash_days || []).length > 0 && <>
              <div className="text-[11px] text-gray-500 mt-2 mb-1">Flash Day ({detail.flash_days.length})</div>
              {detail.flash_days.slice(0, 10).map((f) => (
                <div key={f.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5"><span className="min-w-0 break-words">{fmtDate(f.date)} · {f.title}</span><span className="text-gray-500 shrink-0">{f.status}</span></div>
              ))}
            </>}
          </div>

          {/* Storico economico */}
          <div data-testid="artist-economic">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Storico economico</div>
            {(detail.payments || []).slice(0, 40).map((t) => (
              <div key={t.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                <span className="min-w-0 break-words">{fmtDate(t.created_at)} · {ptypeLabel(t.payment_type)} <span data-testid="pay-source-badge" className={`ml-1 text-[9px] font-semibold ${t.source_label && t.source_label.startsWith("MANUALE") ? "text-violet-700" : "text-gray-400"}`}>{t.source_label}</span></span>
                <span className="text-gray-500 shrink-0 text-right">
                  {fmtEur(t.amount)} · {t.payment_status}
                  {t.refund_status === "refunded" && <span className="text-rose-600"> · rimb. {fmtEur(t.refund_amount)}</span>}
                </span>
              </div>
            ))}
            {(detail.payments || []).length === 0 && <div className="text-xs text-gray-400">Nessun pagamento registrato.</div>}
            {detail.kpi && (detail.payments || []).length > 0 && <div className="text-[11px] text-gray-600 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>Pagato: <b>{fmtEur(detail.kpi.totale_pagato)}</b></span>
              <span>Rimborsato: <b className="text-rose-600">{fmtEur(detail.kpi.totale_rimborsato)}</b></span>
              <span>Netto: <b>{fmtEur(detail.kpi.netto)}</b></span>
            </div>}
          </div>

          {/* Riaccrediti — separati dagli incassi, NON conteggiati nel totale economico */}
          <div data-testid="artist-credit-adjustments">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Riaccrediti / rettifiche saldo <span className="text-[10px] font-normal text-gray-400">(non è un incasso)</span></div>
            {(detail.credit_adjustments || []).length > 0 ? (detail.credit_adjustments).slice(0, 20).map((c) => (
              <div key={c.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5">
                <span className="min-w-0 break-words">{fmtDateTime(c.ts)} · {ST(c.station_type)} · {c.reason}</span>
                <span className={`shrink-0 ${c.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>{c.quantity > 0 ? "+" : ""}{c.quantity} gg</span>
              </div>
            )) : <div className="text-xs text-gray-400">Nessun riaccredito.</div>}
          </div>

          {/* Referral */}
          <div data-testid="artist-referrals">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Referral</div>
            {(detail.referrals || []).length > 0 ? (detail.referrals || []).slice(0, 20).map((r) => (
              <div key={r.id} className="text-xs flex justify-between gap-2 border-b border-gray-50 py-0.5"><span className="min-w-0 break-words">{fmtDate(r.created_at)} · {r.referred_name || "—"}</span><span className="text-gray-500 shrink-0">{r.status}</span></div>
            )) : <div className="text-xs text-gray-400">Nessun referral collegato.</div>}
          </div>

          {/* Contratto e documenti (placeholder P5 futuro) */}
          <div data-testid="artist-contract-placeholder">
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Contratto e documenti</div>
            <div className="text-xs text-gray-400">Sezione in arrivo — nessun documento disponibile.</div>
          </div>

          <button data-testid="artist-detail-close" onClick={() => setDetail(null)} className="btn-outline w-full text-sm">Chiudi</button>
        </div>}
      </Modal>

      <Modal open={!!credit} onClose={() => setCredit(null)} title={`Riaccredito manuale · ${credit?.name || ""}`}>
        <label className="block text-sm mb-2">Tipo postazione
          <select data-testid="credit-station" value={cForm.station_type} onChange={(e) => setCForm({ ...cForm, station_type: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm">
            <option value="open_space">Open Space</option><option value="cabina">Cabina</option>
          </select>
        </label>
        <label className="block text-sm mb-2">Quantità (usa valore negativo per addebitare)
          <input data-testid="credit-qty" type="number" value={cForm.quantity} onChange={(e) => setCForm({ ...cForm, quantity: parseInt(e.target.value || 0, 10) })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" />
        </label>
        <label className="block text-sm mb-3">Motivo (obbligatorio)
          <textarea data-testid="credit-reason" value={cForm.reason} onChange={(e) => setCForm({ ...cForm, reason: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" placeholder="Es. rimborso eccezionale per disservizio" />
        </label>
        <p className="text-sm text-gray-600 mb-3" data-testid="credit-summary">
          Confermi il {cForm.quantity >= 0 ? "riaccredito" : "addebito"} di <b>{Math.abs(cForm.quantity)} {Math.abs(cForm.quantity) === 1 ? "giornata" : "giornate"} {cForm.station_type === "open_space" ? "Open Space" : "Cabina"}</b> a <b>{credit?.name}</b>?
        </p>
        <div className="flex gap-2">
          <button onClick={() => setCredit(null)} disabled={creditBusy} className="btn-outline flex-1 text-sm disabled:opacity-50">Annulla</button>
          <button data-testid="credit-confirm" disabled={creditBusy || !cForm.reason.trim() || !cForm.quantity} onClick={async () => {
            if (creditBusy || !cForm.reason.trim() || !cForm.quantity) return;
            const c = credit;
            setCreditBusy(true);
            const ok = await act(() => api.post("/admin/credit-adjust", { user_id: c.user_id, ...cForm }), "Saldo aggiornato");
            setCreditBusy(false);
            if (ok !== false) {
              setCredit(null);
              if (detail && !detail.loading && detail.profile && detail.profile.id === c.user_id) loadDetail(c.user_id);
            }
          }} className="btn-primary flex-1 text-sm disabled:opacity-60 disabled:cursor-not-allowed">{creditBusy ? "Riaccredito…" : "Conferma riaccredito"}</button>
        </div>
      </Modal>

      {/* Pagamento manuale (MAS MANUAL P2) — incasso fuori Stripe: Contanti / Bonifico / Altro */}
      <Modal open={!!mpay} onClose={() => setMpay(null)} title={`Pagamento manuale · ${mpay?.name || ""}`}>
        <p className="text-xs text-gray-500 mb-3">Registra un incasso ricevuto fuori da Stripe e abilita il prodotto con le stesse regole dell'acquisto online. Non genera pagamenti Stripe. Diverso dal Riaccredito (che rettifica un saldo).</p>
        <label className="block text-sm mb-3">Prodotto / Formula
          <select data-testid="manual-pay-product" value={mForm.product_id} onChange={(e) => setMForm({ ...mForm, product_id: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm">
            <optgroup label="Open Space">
              <option value="os_1">Giornata Singola · 45 €</option>
              <option value="os_5">Pacchetto 5 Giornate · 210 €</option>
              <option value="os_10">Pacchetto 10 Giornate · 390 €</option>
            </optgroup>
            <optgroup label="Cabina Privacy">
              <option value="cab_1">Giornata Singola · 55 €</option>
              <option value="cab_5">Pacchetto 5 Giornate · 260 €</option>
              <option value="cab_10">Pacchetto 10 Giornate · 490 €</option>
            </optgroup>
            <optgroup label="Resident">
              <option value="resident_flex">Resident Flex · 449 €/mese</option>
              <option value="resident_reserved">Resident Reserved · 549 €/mese</option>
            </optgroup>
          </select>
        </label>
        <label className="block text-sm mb-3">Metodo di pagamento
          <select data-testid="manual-pay-method" value={mForm.method} onChange={(e) => setMForm({ ...mForm, method: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm">
            <option value="cash">Contanti</option>
            <option value="bank_transfer">Bonifico</option>
            <option value="other">Altro</option>
          </select>
        </label>
        <label className="block text-sm mb-3">Note (facoltative)
          <textarea data-testid="manual-pay-note" value={mForm.note} onChange={(e) => setMForm({ ...mForm, note: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm" placeholder="Es. incasso in studio" />
        </label>
        <div className="flex gap-2">
          <button onClick={() => setMpay(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="manual-pay-confirm" onClick={() => {
            const m = mpay; setMpay(null);
            act(() => api.post("/admin/manual-payment", { user_id: m.user_id, product_id: mForm.product_id, payment_method: mForm.method, note: mForm.note }), "Pagamento manuale registrato");
          }} className="btn-primary flex-1 text-sm">Registra incasso</button>
        </div>
      </Modal>
      <Modal open={!!artConfirm} onClose={() => setArtConfirm(null)} title={artConfirm?.action === "suspend" ? "Sospendere l'artista?" : "Rifiutare l'artista?"}>
        <p className="text-sm text-gray-600 mb-3">
          {artConfirm?.action === "suspend"
            ? <>Sospendere <b>{artConfirm?.name}</b>? L'artista non potrà utilizzare normalmente le funzioni operative finché non verrà riattivato.</>
            : <>Rifiutare <b>{artConfirm?.name}</b>? L'artista non risulterà approvato.</>}
        </p>
        <div className="flex gap-2">
          <button onClick={() => setArtConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="artist-action-confirm" onClick={() => { const c = artConfirm; setArtConfirm(null); act(() => api.post(`/admin/artists/${c.id}/${c.action}`)); }} className="btn-primary flex-1 text-sm">{artConfirm?.action === "suspend" ? "Sospendi artista" : "Rifiuta artista"}</button>
        </div>
      </Modal>
      {/* Conferma leggera P2 (completa / approva / riattiva / riapri) */}
      <Modal open={!!cwConfirm} onClose={() => setCwConfirm(null)} title={cwConfirm?.title}>
        <p className="text-sm text-gray-600 mb-3">{cwConfirm?.body}</p>
        <div className="flex gap-2">
          <button onClick={() => setCwConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="cw-confirm-action" onClick={() => { const c = cwConfirm; setCwConfirm(null); c.run(); }} className="btn-primary flex-1 text-sm">{cwConfirm?.label}</button>
        </div>
      </Modal>
      {/* Proroga */}
      <Modal open={!!extend} onClose={() => setExtend(null)} title="Proroga scadenza pacchetto">
        <input type="date" data-testid="extend-date" value={extDate} onChange={(e) => setExtDate(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-3" />
        <div className="flex gap-2">
          <button onClick={() => setExtend(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="extend-confirm" onClick={() => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(extDate)) return;
            const p = extend; setExtend(null);
            act(() => api.post(`/admin/packages/${p.id}/extend`, { new_expiry: new Date(extDate + "T00:00:00Z").toISOString() }), "Scadenza aggiornata");
          }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
      {/* Conferma blocco disponibilità */}
      <Modal open={!!blkConfirm} onClose={() => setBlkConfirm(null)} title="Conferma blocco disponibilità">
        {blkConfirm && (
          <>
            <p className="text-sm text-gray-700 mb-2">
              Stai per bloccare <b>{blkConfirm.days}</b> {blkConfirm.days === 1 ? "giornata" : "giornate"} · {scopeLabel[blkConfirm.payload.scope]}.
            </p>
            {blkConfirm.existing_bookings > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-2" data-testid="blk-warning">
                Attenzione: nell'intervallo selezionato sono presenti <b>{blkConfirm.existing_bookings}</b> prenotazioni confermate. Il blocco impedirà nuove prenotazioni ma <b>non annullerà</b> quelle esistenti.
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <button onClick={() => setBlkConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
              <button data-testid="blk-confirm" disabled={blkBusy} onClick={confirmBlock} className="btn-primary flex-1 text-sm">{blkBusy ? "Attendere…" : "Conferma blocco"}</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

/* ---------- TRICO CRM ---------- */
export const TricoTab = ({ data, act }) => {
  const [hist, setHist] = useState({}); // lead_id -> []
  const [leadConfirm, setLeadConfirm] = useState(null); // {id, name, from, to}
  const [retConfirm, setRetConfirm] = useState(null); // {id, name, action}
  const BE = process.env.REACT_APP_BACKEND_URL;
  const loadHist = (id) => {
    if (hist[id]) { setHist({ ...hist, [id]: null }); return; }
    api.get(`/admin/leads/${id}/history`).then((r) => setHist((h) => ({ ...h, [id]: r.data }))).catch(() => {});
  };
  const STATES = ["NUOVO", "CONTATTATO", "CONSULENZA", "CONFERMATO", "TRATTAMENTO CONCLUSO"];
  return (
    <div className="space-y-3" data-testid="trico-tab">
      {(data.trico || []).map((l) => (
        <div key={l.id} className="card p-3" data-testid={`trico-lead-${l.id}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              <div className="font-medium text-sm">{l.name}</div>
              <div className="text-xs text-gray-500">{l.phone}{l.email ? ` · ${l.email}` : ""}</div>
              <div className="text-xs text-gray-500 mt-0.5">{l.treatment_type === "density" ? "Densità" : "Effetto rasato"} · Video: <b>{l.video_review_available ? "Sì" : "No"}</b> · <b>{l.quoted_price === 1000 ? "1.000 €" : "1.250 €"}</b></div>
              {l.referral_invite_id && <div className="text-[10px] text-violet-600">referral {l.referral_id}</div>}
              {(l.photos || []).length > 0 && (
                <div className="flex gap-1 mt-2">
                  {l.photos.map((p, i) => <a key={i} href={BE + p} target="_blank" rel="noreferrer"><img src={BE + p} alt="" className="w-12 h-12 object-cover rounded border" /></a>)}
                </div>
              )}
            </div>
            <span className="badge-chip state-ATTESA shrink-0">{l.status || "NUOVO"}</span>
          </div>
          <div className="flex gap-1 mt-2 flex-wrap">
            {STATES.map((st) => (
              <button key={st} data-testid={`tlead-${st.replace(/ /g, "_")}-${l.id}`} disabled={(l.status || "NUOVO") === st}
                onClick={() => setLeadConfirm({ id: l.id, name: l.name, from: (l.status || "NUOVO"), to: st })}
                className={`text-[11px] py-1 px-2 rounded-full border ${(l.status || "NUOVO") === st ? "bg-violet-600 text-white border-violet-600" : "btn-outline"}`}>{st}</button>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            {l.phone && <a href={`https://wa.me/${(l.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="btn-primary text-[11px] py-1 px-2">WhatsApp</a>}
            <button data-testid={`tlead-hist-${l.id}`} onClick={() => loadHist(l.id)} className="btn-outline text-[11px] py-1 px-2 inline-flex items-center gap-1"><HistoryIcon size={12} />Storico</button>
          </div>
          <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`tlead-retention-${l.id}`}>
            {l.converted ? (
              <>
                <div className="text-[11px] text-green-700" data-testid={`tlead-ret-status-${l.id}`}>Documentazione cliente: conservata secondo la policy del trattamento</div>
                <button data-testid={`tlead-ret-revert-${l.id}`} onClick={() => setRetConfirm({ id: l.id, name: l.name, action: "revert" })} className="btn-outline text-[11px] py-1 px-2 mt-1">Annulla conversione</button>
              </>
            ) : (l.retention_classified && l.retention_class === "lead") ? (
              <>
                <div className="text-[11px] text-amber-700" data-testid={`tlead-ret-status-${l.id}`}>Foto lead: eliminazione automatica dopo 90 giorni</div>
                <button data-testid={`tlead-ret-convert-${l.id}`} onClick={() => setRetConfirm({ id: l.id, name: l.name, action: "mark_converted" })} className="btn-outline text-[11px] py-1 px-2 mt-1">Convertito in cliente</button>
              </>
            ) : (
              <>
                <div className="text-[11px] text-gray-500" data-testid={`tlead-ret-status-${l.id}`}>Classificazione retention non impostata</div>
                {["CONFERMATO", "TRATTAMENTO CONCLUSO"].includes(l.status || "") && (
                  <div className="text-[10px] text-violet-600 mt-0.5">Suggerimento: valuta se convertire in cliente.</div>
                )}
                <div className="flex gap-1 mt-1 flex-wrap">
                  <button data-testid={`tlead-ret-convert-${l.id}`} onClick={() => setRetConfirm({ id: l.id, name: l.name, action: "mark_converted" })} className="btn-primary text-[11px] py-1 px-2">Convertito in cliente</button>
                  <button data-testid={`tlead-ret-lead-${l.id}`} onClick={() => setRetConfirm({ id: l.id, name: l.name, action: "mark_lead" })} className="btn-outline text-[11px] py-1 px-2">Segna come lead (foto elim. 90gg)</button>
                </div>
              </>
            )}
          </div>
          {hist[l.id] && (
            <div className="mt-2 border-t border-gray-100 pt-2 space-y-1" data-testid={`tlead-history-${l.id}`}>
              {hist[l.id].length === 0 && <div className="text-[11px] text-gray-400">Nessun cambio stato registrato.</div>}
              {hist[l.id].map((h) => <div key={h.id} className="text-[11px] text-gray-500">{fmtDateTime(h.timestamp)} · {h.old_status} → <b>{h.new_status}</b></div>)}
            </div>
          )}
        </div>
      ))}
      {(data.trico || []).length === 0 && <p className="text-sm text-gray-400">Nessun lead Tricopigmentazione.</p>}
      <Modal open={!!leadConfirm} onClose={() => setLeadConfirm(null)} title="Cambiare stato del lead?">
        <p className="text-sm text-gray-600 mb-3">{leadConfirm && <>Lead <b>{leadConfirm.name}</b>: <b>{leadConfirm.from}</b> → <b>{leadConfirm.to}</b></>}</p>
        <div className="flex gap-2">
          <button onClick={() => setLeadConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="lead-status-confirm" onClick={() => { const c = leadConfirm; setLeadConfirm(null); act(() => api.put(`/admin/leads/${c.id}/status`, { status: c.to }), "Stato aggiornato"); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
      <Modal open={!!retConfirm} onClose={() => setRetConfirm(null)} title="Conferma classificazione retention">
        <p className="text-sm text-gray-600 mb-3">{retConfirm && <>
          {retConfirm.action === "mark_converted" && <>Segnare <b>{retConfirm.name}</b> come <b>convertito in cliente</b>? Le foto verranno conservate come documentazione del trattamento (nessuna eliminazione automatica).</>}
          {retConfirm.action === "mark_lead" && <>Classificare <b>{retConfirm.name}</b> come <b>lead non convertito</b>? Le foto verranno eliminate automaticamente 90 giorni dopo la data del lead.</>}
          {retConfirm.action === "revert" && <>Annullare la conversione di <b>{retConfirm.name}</b>? La classificazione tornerà "non impostata": nessuna eliminazione automatica finché non riclassifichi esplicitamente il lead.</>}
        </>}</p>
        <div className="flex gap-2">
          <button onClick={() => setRetConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="lead-retention-confirm" onClick={() => { const c = retConfirm; setRetConfirm(null); act(() => api.put(`/admin/leads/${c.id}/retention`, { action: c.action }), "Classificazione aggiornata"); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- FLASH DAY ---------- */
export const FlashTab = ({ data, act }) => {
  const [note, setNote] = useState(null);
  const [txt, setTxt] = useState("");
  const [unpub, setUnpub] = useState(null);
  const [detail, setDetail] = useState(null);
  const [approve, setApprove] = useState(null);
  const flashReason = (r) => ({ artist_cancelled: "Annullato dall'artista" }[r] || r);
  const BE = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="space-y-2" data-testid="flash-tab">
      {(data.flashDays || []).map((fd) => (
        <div key={fd.id} className="card p-3" data-testid={`admin-flash-${fd.id}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
              {fd.image && <img src={fd.image.startsWith("http") ? fd.image : BE + fd.image} alt="" className="w-full max-h-32 object-cover rounded-lg mb-2" />}
              <div className="font-medium text-sm">{fd.title}</div>
              <div className="text-xs text-gray-500">{fd.artist_name} · {fmtDate(fd.date)} · {fd.start_time}{fd.end_time ? `–${fd.end_time}` : ""}</div>
              <div className="text-xs text-gray-400">{ST(fd.station_type)} · views {fd.views || 0} · lead {fd.leads || 0} · condivisioni {fd.shares || 0}</div>
              <p className="text-xs text-gray-600 mt-1">{fd.description}</p>
              {fd.cancel_reason && <div className="text-xs text-red-600 mt-1">Rimosso: {flashReason(fd.cancel_reason)}</div>}
            </div>
            <span className="badge-chip state-ATTESA shrink-0">{fd.status_label}</span>
          </div>
          <button data-testid={`flash-manage-${fd.id}`} onClick={() => setDetail(fd)} className="btn-primary w-full text-sm py-2.5 px-4 mt-2">Gestisci Flash Day</button>
        </div>
      ))}
      {(data.flashDays || []).length === 0 && <p className="text-sm text-gray-400">Nessun Flash Day.</p>}
      <Modal open={!!note} onClose={() => setNote(null)} title={note?.action === "reject" ? "Rifiuta Flash Day" : "Richiedi modifica"}>
        <textarea data-testid="flash-note-text" placeholder="Nota per l'artista" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" value={txt} onChange={(e) => setTxt(e.target.value)} />
        <div className="flex gap-2 mt-3">
          <button onClick={() => setNote(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="flash-note-confirm" onClick={() => { const n = note; setNote(null); act(() => api.post(`/admin/flash-days/${n.id}/${n.action}`, { note: txt })); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
      <Modal open={!!unpub} onClose={() => setUnpub(null)} title="Disattivare il Flash Day?">
        <p className="text-sm text-gray-600 mb-3">Rimuovere <b>{unpub?.title}</b> dalla pubblicazione? Il Flash Day non sarà più visibile pubblicamente. Potrà essere gestito nuovamente dall'Area Admin.</p>
        <div className="flex gap-2">
          <button onClick={() => setUnpub(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="flash-unpublish-confirm" onClick={() => { const f = unpub; setUnpub(null); act(() => api.post(`/admin/flash-days/${f.id}/unpublish`, {})); }} className="btn-primary flex-1 text-sm">Disattiva Flash Day</button>
        </div>
      </Modal>
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Gestisci Flash Day">
        {detail && <div className="space-y-2 text-sm max-h-[75vh] overflow-y-auto">
          {detail.image && <img src={detail.image.startsWith("http") ? detail.image : BE + detail.image} alt="" className="w-full max-h-40 object-cover rounded-lg" />}
          <div className="font-semibold text-base">{detail.title}</div>
          <div className="text-gray-600">Artista: <b>{detail.artist_name}</b></div>
          <div className="text-gray-600">Data: {fmtDate(detail.date)}{detail.start_time ? ` · ${detail.start_time}${detail.end_time ? "–" + detail.end_time : ""}` : ""}</div>
          <div className="text-gray-600">Postazione: {ST(detail.station_type)}</div>
          <div className="text-gray-600">Stato: <b>{detail.status_label}</b></div>
          <div className="text-gray-500 text-xs">Views {detail.views || 0} · Lead {detail.leads || 0} · Condivisioni {detail.shares || 0}</div>
          {detail.description && <p className="text-gray-600">{detail.description}</p>}
          {detail.cancel_reason && <div className="text-red-600 text-xs">Rimosso: {flashReason(detail.cancel_reason)}</div>}
          <div className="flex gap-2 flex-wrap pt-2 border-t border-gray-100">
            {["pending_approval", "changes_requested", "needs_update"].includes(detail.status) && <>
              <button data-testid={`flash-approve-${detail.id}`} onClick={() => { const f = detail; setDetail(null); setApprove(f); }} className="btn-primary text-sm py-2 px-3">Approva</button>
              <button data-testid={`flash-changes-${detail.id}`} onClick={() => { const f = detail; setDetail(null); setNote({ id: f.id, action: "request-changes" }); setTxt(""); }} className="btn-outline text-sm py-2 px-3">Richiedi modifica</button>
              <button data-testid={`flash-reject-${detail.id}`} onClick={() => { const f = detail; setDetail(null); setNote({ id: f.id, action: "reject" }); setTxt(""); }} className="btn-outline text-sm py-2 px-3">Rifiuta</button>
            </>}
            {detail.status === "approved" && <button data-testid={`flash-unpublish-${detail.id}`} onClick={() => { const f = detail; setDetail(null); setUnpub(f); }} className="btn-outline text-sm py-2 px-3">Disattiva</button>}
            <button data-testid="flash-detail-close" onClick={() => setDetail(null)} className="btn-outline text-sm py-2 px-3">Chiudi</button>
          </div>
        </div>}
      </Modal>
      <Modal open={!!approve} onClose={() => setApprove(null)} title="Pubblicare questo Flash Day?">
        <p className="text-sm text-gray-600 mb-3">Pubblicare <b>{approve?.title}</b>? Sarà visibile pubblicamente.</p>
        <div className="flex gap-2">
          <button onClick={() => setApprove(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="flash-approve-confirm" onClick={() => { const f = approve; setApprove(null); act(() => api.post(`/admin/flash-days/${f.id}/approve`, {})); }} className="btn-primary flex-1 text-sm">Pubblica Flash Day</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- VETRINA ---------- */
export const VetrinaTab = ({ data, act, settings }) => {
  const [price, setPrice] = useState("");
  const [vConfirm, setVConfirm] = useState(null); // {title, label, run}
  useEffect(() => { if (settings) setPrice(settings.vetrina_price ?? 0); }, [settings]);
  return (
    <div data-testid="vetrina-tab">
      <div className="card p-4 mb-4">
        <label className="text-sm font-medium">Prezzo Messaggio in Vetrina (€ / 30 min · 0 = gratuito)</label>
        <div className="flex gap-2 mt-2">
          <input data-testid="set-vetrina-price" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="save-vetrina-price" onClick={() => act(() => api.put("/admin/settings", { vetrina_price: parseFloat(price || 0) }), "Prezzo aggiornato")} className="btn-primary text-sm px-4">Salva</button>
        </div>
      </div>
      <div className="space-y-2">
        {(data.vetrina || []).map((m) => {
          const paid = (m.price || 0) > 0; const pending = m.status === "pending_payment";
          return (
            <div key={m.id} className="card p-3" data-testid={`admin-vetrina-${m.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm">"{m.message}"</div>
                  <div className="text-xs text-gray-500">{m.date} · {m.time} · da {m.name} ({m.phone})</div>
                  {m.recipient_name && <div className="text-xs text-gray-400">Per: {m.recipient_name}</div>}
                  <div className="text-xs mt-1">{paid ? <span className="font-semibold text-violet-700">A pagamento · {fmtEur(m.price)}</span> : <span className="text-green-700">Gratuito</span>}{paid && <span className="text-gray-400"> · {pending ? "in attesa pagamento" : (m.paid_at ? "pagamento confermato" : "—")}</span>}</div>
                </div>
                <span className="badge-chip state-ATTESA shrink-0">{m.status}</span>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {pending ? <span className="text-xs text-amber-600">In attesa di pagamento — non pubblicabile.</span> : <>
                  {(m.status === "Richiesta inviata" || m.status === "active") && <button data-testid={`vetrina-approve-${m.id}`} onClick={() => setVConfirm({ title: "Approvare il messaggio in Vetrina?", label: "Approva", run: () => act(() => api.post(`/admin/vetrina/${m.id}/approve`)) })} className="btn-primary text-xs py-1.5 px-3">Approva</button>}
                  {m.status !== "Rifiutata" && m.status !== "Trasmessa" && <button onClick={() => setVConfirm({ title: "Programmare la trasmissione?", label: "Programma", run: () => act(() => api.post(`/admin/vetrina/${m.id}/schedule`)) })} className="btn-outline text-xs py-1.5 px-3">Programma</button>}
                  {m.status !== "Rifiutata" && <button onClick={() => setVConfirm({ title: "Segnare come trasmessa?", label: "Trasmessa", run: () => act(() => api.post(`/admin/vetrina/${m.id}/transmit`)) })} className="btn-outline text-xs py-1.5 px-3">Trasmessa</button>}
                </>}
                {m.status !== "Rifiutata" && <button data-testid={`vetrina-reject-${m.id}`} onClick={() => setVConfirm({ title: "Confermi il rifiuto del messaggio?", body: (paid && m.paid_at) ? `Il messaggio non verrà pubblicato e verrà avviato automaticamente il rimborso di ${fmtEur(m.price)} sul metodo di pagamento originale.` : "Il messaggio non verrà pubblicato.", label: (paid && m.paid_at) ? "Rifiuta e rimborsa" : "Rifiuta messaggio", run: () => act(() => api.post(`/admin/vetrina/${m.id}/reject`), (r) => { const d = r?.data || {}; if (d.refund_status === "refunded") { if (d.already) return "Messaggio già rimborsato."; const amt = (d.refund_currency && String(d.refund_currency).toLowerCase() !== "eur") ? `${d.refund_amount} ${String(d.refund_currency).toUpperCase()}` : fmtEur(d.refund_amount); return `Messaggio rifiutato · rimborsati ${amt}.`; } return "Messaggio rifiutato."; }) })} className="btn-outline text-xs py-1.5 px-3">Rifiuta</button>}
              </div>
            </div>
          );
        })}
        {(data.vetrina || []).length === 0 && <p className="text-sm text-gray-400">Nessun messaggio in vetrina.</p>}
      </div>
      <Modal open={!!vConfirm} onClose={() => setVConfirm(null)} title={vConfirm?.title}>
        {vConfirm?.body && <p className="text-sm text-gray-600 mb-3" data-testid="vetrina-confirm-body">{vConfirm.body}</p>}
        <div className="flex gap-2 mt-1">
          <button onClick={() => setVConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="vetrina-confirm-action" onClick={() => { const c = vConfirm; setVConfirm(null); c.run(); }} className="btn-primary flex-1 text-sm">{vConfirm?.label}</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- REFERRAL ---------- */
export const ReferralTab = ({ data, act, filter, setFilter }) => {
  const [confirm, setConfirm] = useState(null);
  const STLAB = { IN_ATTESA: "In attesa", MATURATO: "Maturato · da consegnare", CONSEGNATO: "Consegnato" };
  const filters = [["all", "Tutti"], ["pending", "In attesa"], ["matured", "Maturati"], ["delivered", "Consegnati"], ["trico", "Trico"], ["coworking", "Coworking"]];
  return (
    <div data-testid="referral-tab">
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {filters.map(([k, l]) => (
          <button key={k} data-testid={`ref-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${filter === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2">
        {(data.vouchers || []).map((v) => (
          <div key={v.id} className="card p-3" data-testid={`voucher-${v.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-medium text-sm">Buono Amazon {fmtEur(v.value)} · {v.program === "trico" ? "Tricopigmentazione" : "Coworking"}</div>
                <div className="text-xs text-gray-500">Invitante: {v.inviter_name} ({v.inviter_phone || v.inviter_email})</div>
                <div className="text-xs text-gray-500">Invitato: {v.invitee_name || v.invitee_contact || "—"}</div>
                <div className="text-[11px] text-gray-400">Codice {v.code} · creato {fmtDate(v.created_at)}{v.matured_at ? ` · maturato ${fmtDate(v.matured_at)}` : ""}{v.delivered_at ? ` · consegnato ${fmtDate(v.delivered_at)}` : ""}</div>
              </div>
              <span className={`badge-chip shrink-0 ${v.status === "MATURATO" ? "state-ULTIMA" : v.status === "CONSEGNATO" ? "state-DISPONIBILE" : "state-ATTESA"}`}>{STLAB[v.status]}</span>
            </div>
            {v.status === "MATURATO" && <button data-testid={`deliver-${v.id}`} onClick={() => setConfirm(v)} className="btn-primary text-xs py-1.5 px-3 mt-2">Segna come consegnato</button>}
          </div>
        ))}
        {(data.vouchers || []).length === 0 && <p className="text-sm text-gray-400">Nessun premio in questa vista.</p>}
      </div>
      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Conferma consegna premio">
        <p className="text-sm text-gray-600 mb-3">Confermi di aver consegnato il Buono Amazon {fmtEur(confirm?.value)} a {confirm?.inviter_name}? L'azione è definitiva.</p>
        <div className="flex gap-2">
          <button onClick={() => setConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="deliver-confirm" onClick={() => { const v = confirm; setConfirm(null); act(() => api.post(`/admin/referral-vouchers/${v.id}/deliver`), "Premio consegnato"); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- PAGAMENTI ---------- */
export const PaymentsTab = ({ data, act, filter, setFilter }) => {
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null);
  const filters = [["all", "Tutti"], ["paid", "Pagati"], ["pending", "In attesa"], ["failed", "Falliti"], ["reconcile", "Da verificare"]];
  const PS = { paid: ["Pagato", "text-green-600"], pending: ["In attesa", "text-amber-600"], processing: ["In elaborazione", "text-amber-600"], failed: ["Fallito", "text-red-600"], cancelled: ["Annullato", "text-gray-500"], expired: ["Checkout non completato", "text-gray-500"] };
  const Row = ({ label, value }) => (value == null || value === "" ? null : (
    <div className="flex justify-between gap-3 text-xs py-1 border-b border-gray-50">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 text-right break-all">{value}</span>
    </div>
  ));
  return (
    <div data-testid="payments-tab">
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {filters.map(([k, l]) => (
          <button key={k} data-testid={`tx-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${filter === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2">
        {(data.transactions || []).map((t) => {
          const ps = PS[t.payment_status] || [t.payment_status, "text-gray-500"];
          return (
            <button key={t.id} onClick={() => setDetail(t)} className="w-full text-left card p-3 active:bg-gray-50" data-testid={`tx-${t.session_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm">{fmtEur(t.amount)} · {(t.currency || "eur").toUpperCase()}</div>
                  <div className="text-xs text-gray-500 truncate">{t.product_name || t.payment_type || t.kind} · {t.customer?.name || t.object_id || "—"}</div>
                  <div className="text-[11px] text-gray-400">{fmtDateTime(t.completed_at || t.updated_at || t.created_at)} · {(t.session_id || "").slice(0, 14)}…</div>
                  {t.last_error && <div className="text-[11px] text-red-500 mt-0.5">Errore: {String(t.last_error).slice(0, 80)}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-semibold ${ps[1]}`}>{ps[0]}</div>
                  <div className="text-[10px] text-gray-400">fulfillment: {t.fulfillment_status || "—"}</div>
                  {t.source_label && <div data-testid="tx-manual-badge" className="text-[9px] font-semibold text-violet-700">{t.source_label}</div>}
                  {t.stripe_mode && <div className={`text-[9px] font-semibold ${t.stripe_mode === "LIVE" ? "text-red-600" : "text-amber-600"}`}>{t.stripe_mode}</div>}
                  {t.needs_reconciliation && <div className="text-[10px] text-red-600 font-semibold mt-0.5">DA VERIFICARE</div>}
                </div>
              </div>
              {t.needs_reconciliation && <span data-testid={`retry-${t.session_id}`} onClick={(e) => { e.stopPropagation(); setConfirm(t); }} className="inline-block btn-primary text-xs py-1.5 px-3 mt-2">Riprova fulfillment</span>}
            </button>
          );
        })}
        {(data.transactions || []).length === 0 && <p className="text-sm text-gray-400">Nessuna transazione in questa vista.</p>}
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Dettaglio transazione">
        {detail && <div className="space-y-0.5" data-testid="tx-detail">
          <Row label="Importo" value={`${fmtEur(detail.amount)} · ${(detail.currency || "eur").toUpperCase()}`} />
          <Row label="Stato pagamento" value={(PS[detail.payment_status] || [detail.payment_status])[0]} />
          <Row label="Stato fulfillment" value={detail.fulfillment_status} />
          <Row label="Tipo acquisto" value={detail.payment_type || detail.kind} />
          <Row label="Prodotto" value={detail.product_name} />
          <Row label="Cliente" value={detail.customer?.name} />
          <Row label="Telefono" value={detail.customer?.phone} />
          <Row label="Email" value={detail.customer?.email} />
          <Row label="Data / ora" value={fmtDateTime(detail.completed_at || detail.updated_at || detail.created_at)} />
          <Row label="Origine pagamento" value={detail.source_label || (detail.payment_method === "cash" ? "MANUALE / CONTANTI" : `Stripe ${detail.stripe_mode || ""}`)} />
          <Row label="Riferimento" value={detail.session_id} />
          <Row label="Riferimento oggetto" value={detail.object_id || detail.course_id || detail.package_id || detail.vetrina_message_id || detail.booking_id} />
          <Row label="Rimborso" value={detail.refund_amount != null ? `${detail.refund_amount} €${detail.refund_pct != null ? ` (${detail.refund_pct}%)` : ""}` : null} />
          <Row label="Errore" value={detail.last_error} />
          <div className="pt-3">
            <button onClick={() => setDetail(null)} className="btn-outline w-full text-sm">Chiudi</button>
          </div>
        </div>}
      </Modal>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Riprova fulfillment">
        <p className="text-sm text-gray-600 mb-3">Verrà verificato il pagamento su Stripe e riapplicato il beneficio (idempotente). Confermi?</p>
        <div className="flex gap-2">
          <button onClick={() => setConfirm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="retry-confirm" onClick={() => { const t = confirm; setConfirm(null); act(() => api.post(`/admin/transactions/${t.session_id}/retry-fulfillment`), "Riconciliazione eseguita"); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- CORSI (non attivo) ---------- */
export const CorsiTab = ({ data, act, courses, loadCourseEnrollments, enrollments }) => {
  const [form, setForm] = useState(null); // course being created/edited
  const [openEnr, setOpenEnr] = useState(null); // course id showing enrollments
  const [refund, setRefund] = useState(null);
  const [delCourse, setDelCourse] = useState(null);
  const pctFor = (e) => {
    const c = (courses || []).find((x) => x.id === e?.course_id);
    if (!c || !c.date) return null;
    const diff = new Date((c.date || "").slice(0, 10) + "T23:59:00") - new Date();
    if (Math.floor(diff / 86400000) >= 10) return 80;
    if (diff / 3600000 >= 72) return 50;
    return 0;
  };
  const blank = { title: "", category: "Masterclass", date: "", time: "", end_time: "", teacher: "", description: "", spots: 10, price: 0, image: "", active: true };
  const inp = "w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm";
  const save = () => {
    const f = form; setForm(null);
    if (f.id) act(() => api.put(`/admin/courses/${f.id}`, f), "Corso aggiornato");
    else act(() => api.post("/admin/courses", f), "Corso creato");
  };
  return (
    <div data-testid="corsi-tab">
      <button data-testid="course-new" onClick={() => setForm({ ...blank })} className="btn-primary text-sm w-full mb-4">+ Nuovo corso</button>
      <div className="space-y-3">
        {(courses || []).map((c) => (
          <div key={c.id} className="card p-3" data-testid={`admin-course-${c.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{c.title}</div>
                <div className="text-xs text-gray-500">{c.category} · {fmtDate(c.date)}{c.time ? ` · ${fmtTimeRange(c.time)}` : ""}{c.teacher ? ` · ${c.teacher}` : ""}</div>
                <div className="text-xs text-gray-500 mt-0.5">{fmtEur(c.price)} · posti {c.seats_left}/{c.spots} · {c.active === false ? "non attivo" : "attivo"}</div>
              </div>
              <span className="badge-chip state-DISPONIBILE shrink-0">{c.enroll_paid} iscritti</span>
            </div>
            <div className="text-[11px] text-gray-400 mt-1">Incassi {fmtEur(c.revenue)} · Rimborsi {fmtEur(c.refunds)} · Visite {c.visits} (docente {c.teacher_visits}) · Iscr. docente {c.teacher_enroll_paid} ({fmtEur(c.teacher_revenue)})</div>
            <div className="text-[11px] text-violet-600 mt-1 break-all">Link docente: {window.location.origin}/corsi/{c.id}?t={c.teacher_code}</div>
            <div className="flex gap-1 mt-2 flex-wrap">
              <button data-testid={`course-edit-${c.id}`} onClick={() => setForm({ ...blank, ...c })} className="btn-outline text-xs py-1 px-2">Modifica</button>
              <button data-testid={`course-enroll-list-${c.id}`} onClick={() => { setOpenEnr(openEnr === c.id ? null : c.id); if (openEnr !== c.id) loadCourseEnrollments(c.id); }} className="btn-outline text-xs py-1 px-2">Iscrizioni</button>
              <button data-testid={`course-del-${c.id}`} onClick={() => setDelCourse(c)} className="btn-outline text-xs py-1 px-2">Elimina</button>
            </div>
            {openEnr === c.id && (
              <div className="mt-2 border-t border-gray-100 pt-2 space-y-1" data-testid={`enrollments-${c.id}`}>
                {(enrollments || []).length === 0 && <div className="text-[11px] text-gray-400">Nessuna iscrizione.</div>}
                {(enrollments || []).map((e) => (
                  <div key={e.id} className="flex justify-between items-center text-[11px]">
                    <span className="min-w-0 truncate">{e.name} · {e.phone} · {fmtEur(e.amount)}{e.teacher_code ? " · docente" : ""}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className={e.status === "paid" ? "text-green-600" : e.status === "refunded" ? "text-gray-500" : "text-amber-600"}>{e.status}</span>
                      {e.status === "paid" && (e.amount || 0) > 0
                        ? <button data-testid={`refund-${e.id}`} onClick={() => setRefund(e)} className="btn-outline py-0.5 px-1.5">Rimborsa</button>
                        : (e.amount || 0) === 0 && <span className="text-green-600">Gratuito</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {(courses || []).length === 0 && <p className="text-sm text-gray-400">Nessun corso. Creane uno.</p>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Modifica corso" : "Nuovo corso"}>
        {form && <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          <input data-testid="course-title" className={inp} placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className={inp} placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <label className="block text-xs text-gray-500">Data (gg/mm/aaaa)<input data-testid="course-date" type="date" className={inp} value={(form.date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
          <input className={inp} placeholder="Orario (es. 10:00–17:00)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          <input data-testid="course-end-time" type="time" className={inp} placeholder="Orario di fine (opzionale)" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          <p className="text-[11px] text-gray-400 -mt-1">Orario di fine opzionale: se vuoto, il corso scade dopo le 23:59 della data.</p>
          <input className={inp} placeholder="Docente" value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} />
          <textarea className={inp} placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <label className="block text-xs text-gray-500 flex-1">Numero partecipanti<input data-testid="course-spots" type="number" min="1" className={inp} placeholder="Posti" value={form.spots} onChange={(e) => setForm({ ...form, spots: parseInt(e.target.value || 0, 10) })} /></label>
            <label className="block text-xs text-gray-500 flex-1">Importo (€)<input data-testid="course-price" type="number" min="0" step="0.01" className={inp} placeholder="Prezzo €" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} /></label>
          </div>
          <CourseImagePicker value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Attivo (visibile e iscrivibile)</label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setForm(null)} className="btn-outline flex-1 text-sm">Annulla</button>
            <button data-testid="course-save" onClick={save} className="btn-primary flex-1 text-sm">Salva</button>
          </div>
        </div>}
      </Modal>

      <Modal open={!!refund} onClose={() => setRefund(null)} title="Conferma rimborso iscrizione">
        {refund && (() => {
          const amount = refund.amount || 0;
          const pct = pctFor(refund);
          const policyAmt = pct == null ? null : Math.round(amount * pct) / 100;
          const cTitle = ((courses || []).find((x) => x.id === refund.course_id) || {}).title || "—";
          return (
            <div className="space-y-2 text-sm">
              <div className="text-gray-600">Corso: <span className="font-semibold text-gray-800">{cTitle}</span></div>
              <div className="text-gray-600">Partecipante: <span className="font-semibold text-gray-800">{refund.name || "—"}</span></div>
              <div className="text-gray-600">Importo pagato: <span className="font-semibold text-gray-800">{fmtEur(amount)}</span></div>
              <div className="rounded-xl bg-gray-50 p-3 text-xs space-y-1">
                <div>Rimborso da policy: <b>{pct == null ? "—" : pct + "%"}</b>{policyAmt != null && <> → <b>{fmtEur(policyAmt)}</b> (trattenuto {fmtEur(Math.round((amount - policyAmt) * 100) / 100)})</>}</div>
                <div>Rimborso integrale (annullo MAS): <b>100%</b> → <b>{fmtEur(amount)}</b></div>
              </div>
              <p className="text-xs text-red-600 font-medium">Il rimborso verrà eseguito tramite Stripe e non deve essere confermato per errore.</p>
              <div className="flex flex-col gap-2 pt-1">
                <button data-testid="refund-policy" onClick={() => { const e = refund; setRefund(null); loadCourseEnrollments(e.course_id); act(() => api.post(`/admin/courses/enrollments/${e.id}/refund`, {}), "Rimborso eseguito"); }} className="btn-primary text-sm">{policyAmt != null ? `Conferma rimborso ${fmtEur(policyAmt)} (policy)` : "Conferma rimborso da policy"}</button>
                <button data-testid="refund-full" onClick={() => { const e = refund; setRefund(null); loadCourseEnrollments(e.course_id); act(() => api.post(`/admin/courses/enrollments/${e.id}/refund`, { full: true }), "Rimborso integrale eseguito"); }} className="btn-outline text-sm">Conferma rimborso integrale {fmtEur(amount)}</button>
                <button onClick={() => setRefund(null)} className="btn-outline text-sm">Annulla</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={!!delCourse} onClose={() => setDelCourse(null)} title="Eliminare definitivamente questo corso?">
        <p className="text-sm text-gray-600 mb-1">Corso: <span className="font-semibold text-gray-800">{delCourse?.title}</span></p>
        <p className="text-sm text-gray-600 mb-3">Il corso verrà eliminato definitivamente. Questa operazione non può essere annullata.</p>
        <div className="flex gap-2">
          <button onClick={() => setDelCourse(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="course-del-confirm" onClick={() => { const c = delCourse; setDelCourse(null); act(() => api.delete(`/admin/courses/${c.id}`), "Corso eliminato"); }} className="btn-primary flex-1 text-sm bg-red-600 hover:bg-red-700 border-red-600">Elimina definitivamente</button>
        </div>
      </Modal>
    </div>
  );
};

/* ---------- IMPOSTAZIONI ---------- */
/* ---------- GESTIONALE — Costi / Break-even / Margine (P5 §113-126) ---------- */
export const ResidentiTab = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState("all");
  useEffect(() => { api.get("/admin/residents").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const stLabel = (r) => (r.expired ? "Scaduto" : (r.is_current && (r.days_remaining || 0) <= 7) ? "In scadenza"
    : r.is_current ? "Attivo" : r.status === "held" ? "In attesa" : r.status === "expired" ? "Scaduto" : "Programmato");
  const filtered = rows.filter((r) => f === "all"
    || (f === "reserved" && r.type === "RESIDENT_RESERVED")
    || (f === "flex" && r.type === "RESIDENT_FLEX"));
  return (
    <div data-testid="residenti-tab">
      <div className="flex gap-2 mb-3">
        {[["all", "Tutti"], ["reserved", "Reserved"], ["flex", "Flex"]].map(([k, l]) => (
          <button key={k} data-testid={`res-filter-${k}`} onClick={() => setF(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${f === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-600"}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-sm text-gray-400" data-testid="residenti-empty">Nessun Resident in questa vista.</p>}
      <div className="space-y-2">
        {filtered.map((r, i) => (
          <div key={`${r.artist_id}-${i}`} className="card p-3" data-testid="res-row">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm min-w-0 truncate">{r.artist_name}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${r.type === "RESIDENT_RESERVED" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{r.type === "RESIDENT_RESERVED" ? "Reserved" : "Flex"}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1 break-words">{fmtDate(r.start_date)}→{fmtDate(r.last_usable_date)} · {stLabel(r)}{r.is_current ? ` · ${r.days_remaining} gg residui` : ""}</div>
            <div className="text-xs text-gray-500 mt-0.5 break-words">
              {r.type === "RESIDENT_RESERVED"
                ? `Posto garantito Open Space · prenotati ${r.booked_days ?? 0} gg · pagato ${fmtEur(r.price)}`
                : `Crediti residui: ${r.credits_remaining} · pagato ${fmtEur(r.price)}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const GestionaleTab = ({ period }) => {
  const [costs, setCosts] = useState([]);
  const [sum, setSum] = useState(null);
  const [lp, setLp] = useState(period || { preset: "30d", start: "", end: "" });
  const todayIso = new Date().toISOString().slice(0, 10);
  const emptyForm = { type: "fixed", category: "", amount: "", date: todayIso, note: "", recurrence: "monthly", start_date: todayIso, end_date: "" };
  const [form, setForm] = useState(emptyForm);
  const [delCost, setDelCost] = useState(null);
  const [endCost, setEndCost] = useState(null);
  const [editCost, setEditCost] = useState(null);
  const load = () => {
    api.get("/admin/costs").then((r) => setCosts(r.data)).catch(() => {});
    const qs = lp.preset === "custom" ? `preset=custom&start=${lp.start}&end=${lp.end}` : `preset=${lp.preset}`;
    api.get(`/admin/costs/summary?${qs}`).then((r) => setSum(r.data)).catch(() => {});
  };
  useEffect(load, [lp]); // eslint-disable-line
  const add = async () => {
    if (!form.category || !form.amount) return toast.error("Inserisci categoria e importo");
    const isFixed = form.type === "fixed";
    const body = isFixed
      ? { type: "fixed", category: form.category, amount: parseFloat(form.amount), note: form.note,
          recurrence: form.recurrence, start_date: form.start_date, date: form.start_date,
          ...(form.recurrence !== "once" && form.end_date ? { end_date: form.end_date } : {}) }
      : { type: form.type, category: form.category, amount: parseFloat(form.amount), date: form.date, note: form.note };
    try { await api.post("/admin/costs", body); toast.success("Costo aggiunto"); setForm({ ...emptyForm, type: form.type, recurrence: form.recurrence }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/costs/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const endNow = async () => {
    if (!endCost) return;
    try { await api.post(`/admin/costs/${endCost.id}/end`, { end_date: endCost._end || todayIso }); toast.success("Costo terminato"); setEndCost(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    if (!editCost) return;
    const b = { category: editCost.category, amount: parseFloat(editCost.amount), note: editCost.note || "" };
    if (editCost.type === "fixed" && editCost.recurrence) {
      b.recurrence = editCost.recurrence; b.start_date = editCost.start_date;
      b.end_date = editCost.recurrence !== "once" ? (editCost.end_date || null) : null;
    }
    try { await api.put(`/admin/costs/${editCost.id}`, b); toast.success("Costo aggiornato"); setEditCost(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const inp = "w-full border border-gray-200 rounded-xl px-3 py-2 mt-1";
  const typeLabel = { fixed: "Fisso", variable: "Variabile", occasional: "Occasionale / investimento" };
  const recLabel = { monthly: "Mensile", yearly: "Annuale", once: "Una tantum" };
  const isRecurring = (c) => c.type === "fixed" && c.recurrence && c.recurrence !== "once";
  const ar = sum?.revenue_by_area || {};
  const m = sum ? sum.management_margin : 0;
  const mState = m > 0 ? "pos" : m < 0 ? "neg" : "zero";
  const cov = sum?.cost_coverage_percentage;
  return (
    <div data-testid="gestionale-tab">
      {/* 1. SELETTORE PERIODO */}
      <PeriodFilter period={lp} setPeriod={setLp} />

      {sum ? <>
        {/* 2. INCASSI */}
        <div className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Incassi</div>
        <div className="card p-4 mb-4 space-y-2" data-testid="gest-incassi">
          <Row label="Ricavi lordi" value={fmtEur(sum.gross_revenue)} testid="gest-gross" />
          <Row label="Rimborsi" value={`− ${fmtEur(sum.refunds)}`} sub={sum.refunds > 0 ? `Vetrina ${fmtEur(sum.refunds_vetrina)} · Corsi ${fmtEur(sum.refunds_corsi)}` : null} testid="gest-refunds" />
          <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
            <span className="text-sm font-semibold">Ricavi netti</span>
            <span className="text-base font-bold" data-testid="gest-net">{fmtEur(sum.net_revenue)}</span>
          </div>
        </div>

        {/* 3. COSTI */}
        <div className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Costi</div>
        <div className="card p-4 mb-4 space-y-2" data-testid="gest-costi">
          <Row label="Costi fissi" value={fmtEur(sum.costi_fissi)} testid="gest-fissi" />
          <Row label="Costi variabili" value={fmtEur(sum.costi_variabili)} testid="gest-variabili" />
          <Row label="Occasionali / investimenti" value={fmtEur(sum.costi_occasionali)} testid="gest-occasionali" />
          <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
            <span className="text-sm font-semibold">Costi totali</span>
            <span className="text-base font-bold" data-testid="gest-costi-totali">{fmtEur(sum.total_costs)}</span>
          </div>
        </div>

        {/* 4. RISULTATO */}
        <div className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Risultato</div>
        <div className={`card p-4 mb-4 space-y-2 border ${mState === "pos" ? "border-emerald-200 bg-emerald-50/40" : mState === "neg" ? "border-red-200 bg-red-50/40" : "border-gray-200 bg-gray-50/50"}`} data-testid="gest-risultato">
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold">Margine gestionale</span>
            <span className={`text-lg font-bold ${mState === "pos" ? "text-emerald-700" : mState === "neg" ? "text-red-700" : "text-gray-800"}`} data-testid="gest-margine">
              {mState === "pos" ? `▲ +${fmtEur(m)}` : mState === "neg" ? `▼ −${fmtEur(Math.abs(m))}` : fmtEur(0)}
            </span>
          </div>
          <div className="text-xs font-medium" data-testid="gest-margine-stato">
            {mState === "pos" ? "In utile gestionale" : mState === "neg" ? "In perdita gestionale" : "Pareggio gestionale"}
          </div>
          <div className="border-t border-gray-100 pt-2 grid grid-cols-1 gap-2">
            <Row label="Copertura costi" value={cov == null ? "—" : `${cov.toLocaleString("it-IT", { maximumFractionDigits: 1 })} %`} testid="gest-coverage" />
            <Row label="Ricavi mancanti al pareggio" value={fmtEur(sum.remaining_to_break_even)} sub={sum.remaining_to_break_even === 0 ? "Break-even raggiunto ✓" : null} testid="gest-remaining" />
            <Row label="Break-even (costi totali)" value={fmtEur(sum.break_even)} testid="gest-breakeven" />
            {sum.giornate_equivalenti != null && (
              <Row label="Giornate equivalenti al pareggio" value={`≈ ${sum.giornate_equivalenti} gg`} sub={sum.prezzo_medio_giornata != null ? `prezzo medio reale ${fmtEur(sum.prezzo_medio_giornata)}/gg` : null} testid="gest-giornate-eq" />
            )}
            {sum.giornate_equivalenti == null && (
              <Row label="Giornate equivalenti al pareggio" value="—" sub="dati coworking insufficienti nel periodo" testid="gest-giornate-eq" />
            )}
          </div>
        </div>

        {/* 5. RICAVI PER AREA */}
        <div className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Ricavi per area</div>
        <div className="card p-4 mb-4 space-y-2" data-testid="gest-aree">
          <Row label="Coworking — giornate singole" value={fmtEur(ar.cw_giornate_singole)} testid="gest-ar-single" />
          <Row label="Coworking — pacchetti" value={fmtEur(ar.cw_pacchetti)} testid="gest-ar-pkg" />
          <Row label="Coworking — spostamenti tardivi" value={fmtEur(ar.cw_spostamenti)} testid="gest-ar-move" />
          <Row label="Vetrina" value={fmtEur(ar.vetrina)} testid="gest-ar-vetrina" />
          <Row label="Corsi / Eventi" value={fmtEur(ar.corsi)} testid="gest-ar-corsi" />
          <Row label="Resident Flex" value={fmtEur(ar.resident_flex)} testid="gest-ar-flex" />
          <Row label="Resident Reserved" value={fmtEur(ar.resident_reserved)} testid="gest-ar-reserved" />
          <Row label="Resident — Totale" value={fmtEur(ar.resident_total)} testid="gest-ar-resident-total" />
          {ar.altro > 0 && <Row label="Altre categorie" value={fmtEur(ar.altro)} testid="gest-ar-altro" />}
        </div>
      </> : <p className="text-sm text-gray-400 mb-4">Caricamento…</p>}

      <p className="text-[11px] text-gray-400 mb-3">Strumento gestionale interno: NON genera fatture né transazioni Stripe e non sostituisce la contabilità fiscale. Ricavi = pagamenti confermati (al netto dei rimborsi); costi inseriti manualmente.</p>

      {/* 6. GESTIONE COSTI */}
      <div className="mb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">Gestione costi</div>
      <div className="card p-4 mb-4 space-y-2">
        <div className="font-semibold text-sm">Aggiungi costo</div>
        <select data-testid="cost-type" className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          <option value="fixed">Fisso</option>
          <option value="variable">Variabile</option>
          <option value="occasional">Occasionale / investimento</option>
        </select>
        {form.type === "fixed" && (
          <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-3 space-y-2" data-testid="cost-fixed-block">
            <label className="block text-xs text-gray-500">Ricorrenza
              <select data-testid="cost-recurrence" className={inp} value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}>
                <option value="monthly">Mensile</option>
                <option value="yearly">Annuale</option>
                <option value="once">Una tantum</option>
              </select>
            </label>
            <label className="block text-xs text-gray-500">Decorrenza (gg/mm/aaaa)
              <input data-testid="cost-start-date" type="date" className={inp} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </label>
            {form.recurrence !== "once" && (
              <label className="block text-xs text-gray-500">Fine (opzionale)
                <input data-testid="cost-end-date" type="date" className={inp} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </label>
            )}
            <p className="text-[11px] text-violet-700/70">{form.recurrence === "monthly" ? "Imputato una volta per ogni mese di attività." : form.recurrence === "yearly" ? "Imputato una volta per ogni anno di ricorrenza." : "Imputato una sola volta alla data di decorrenza."}</p>
          </div>
        )}
        <input data-testid="cost-category" className={inp} placeholder="Categoria (es. Affitto, Utenze, Commercialista…)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <div className="flex gap-2">
          <label className="block text-xs text-gray-500 flex-1">Importo (€)<input data-testid="cost-amount" type="number" step="0.01" className={inp} placeholder="Importo €" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
          {form.type !== "fixed" && (
            <label className="block text-xs text-gray-500 flex-1">Data (gg/mm/aaaa)<input data-testid="cost-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
          )}
        </div>
        <input className={inp} placeholder="Nota (opzionale)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <button data-testid="cost-add" onClick={add} className="btn-primary w-full text-sm">Aggiungi costo</button>
      </div>
      <div className="space-y-2">
        {costs.map((c) => {
          const rec = c.type === "fixed" && c.recurrence;
          const terminated = rec && c.active === false;
          return (
          <div key={c.id} className="card p-3 flex justify-between items-center gap-2" data-testid={`cost-${c.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.category} <span className="text-xs text-gray-400">· {typeLabel[c.type] || c.type}{rec ? ` · ${recLabel[c.recurrence] || c.recurrence}` : ""}</span></div>
              <div className="text-xs text-gray-500">
                {rec ? <>Dal {fmtDate(c.start_date || c.date)}{c.end_date ? ` · Fine ${fmtDate(c.end_date)}` : ""}</> : fmtDate(c.date)}
                {c.note ? ` · ${c.note}` : ""}
              </div>
              {rec && (
                <span data-testid={`cost-status-${c.id}`} className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${terminated ? "bg-gray-200 text-gray-600" : "bg-emerald-100 text-emerald-700"}`}>
                  {terminated ? "TERMINATO" : "ATTIVO"}
                </span>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-sm font-semibold">{fmtEur(c.amount)}</span>
              <div className="flex items-center gap-1">
                <button data-testid={`cost-edit-${c.id}`} onClick={() => setEditCost({ ...c, amount: String(c.amount), end_date: c.end_date || "", start_date: c.start_date || c.date || "" })} className="btn-outline text-xs py-1 px-2">Modifica</button>
                {rec && !terminated && (
                  <button data-testid={`cost-end-${c.id}`} onClick={() => setEndCost({ ...c, _end: todayIso })} className="btn-outline text-xs py-1 px-2">Termina</button>
                )}
                {!rec && (
                  <button data-testid={`cost-del-${c.id}`} onClick={() => setDelCost(c)} className="btn-outline text-xs py-1 px-2">Elimina</button>
                )}
              </div>
            </div>
          </div>
          );
        })}
        {costs.length === 0 && <p className="text-sm text-gray-400">Nessun costo inserito. Aggiungi i costi reali per calcolare break-even e margine.</p>}
      </div>

      {/* Modale ELIMINA (solo costi non ricorrenti) */}
      <Modal open={!!delCost} onClose={() => setDelCost(null)} title="Eliminare questo costo?">
        <p className="text-sm text-gray-600 mb-3">{delCost && <>Rimuovere <b>{delCost.category}</b> <span className="text-gray-400">({typeLabel[delCost.type] || delCost.type})</span> di <b>{fmtEur(delCost.amount)}</b> dal Gestionale?</>}</p>
        <div className="flex gap-2">
          <button onClick={() => setDelCost(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="cost-del-confirm" onClick={() => { const c = delCost; setDelCost(null); del(c.id); }} className="btn-primary flex-1 text-sm bg-red-600 hover:bg-red-700 border-red-600">Elimina costo</button>
        </div>
      </Modal>

      {/* Modale TERMINA (costi ricorrenti) */}
      <Modal open={!!endCost} onClose={() => setEndCost(null)} title="Terminare questo costo ricorrente?">
        {endCost && <>
          <p className="text-sm text-gray-600 mb-3">Interrompe le occorrenze <b>future</b> di <b>{endCost.category}</b> ({recLabel[endCost.recurrence] || endCost.recurrence}). Lo storico precedente resta invariato.</p>
          <label className="block text-xs text-gray-500 mb-3">Data fine
            <input data-testid="cost-end-date-input" type="date" className={inp} value={endCost._end} onChange={(e) => setEndCost({ ...endCost, _end: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <button onClick={() => setEndCost(null)} className="btn-outline flex-1 text-sm">Annulla</button>
            <button data-testid="cost-end-confirm" onClick={endNow} className="btn-primary flex-1 text-sm">Termina costo</button>
          </div>
        </>}
      </Modal>

      {/* Modale MODIFICA */}
      <Modal open={!!editCost} onClose={() => setEditCost(null)} title="Modifica costo">
        {editCost && <div className="space-y-2">
          <label className="block text-xs text-gray-500">Categoria<input data-testid="cost-edit-category" className={inp} value={editCost.category} onChange={(e) => setEditCost({ ...editCost, category: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Importo (€)<input data-testid="cost-edit-amount" type="number" step="0.01" className={inp} value={editCost.amount} onChange={(e) => setEditCost({ ...editCost, amount: e.target.value })} /></label>
          {editCost.type === "fixed" && editCost.recurrence && <>
            {editCost.has_matured_occurrence && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1" data-testid="cost-edit-locked-hint">Per modificare ricorrenza o decorrenza, termina questo costo e creane uno nuovo.</p>
            )}
            <label className="block text-xs text-gray-500">Ricorrenza
              <select data-testid="cost-edit-recurrence" disabled={!!editCost.has_matured_occurrence} className={`${inp} ${editCost.has_matured_occurrence ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`} value={editCost.recurrence} onChange={(e) => setEditCost({ ...editCost, recurrence: e.target.value })}>
                <option value="monthly">Mensile</option>
                <option value="yearly">Annuale</option>
                <option value="once">Una tantum</option>
              </select>
            </label>
            <label className="block text-xs text-gray-500">Decorrenza<input data-testid="cost-edit-start" type="date" disabled={!!editCost.has_matured_occurrence} className={`${inp} ${editCost.has_matured_occurrence ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`} value={editCost.start_date} onChange={(e) => setEditCost({ ...editCost, start_date: e.target.value })} /></label>
            {editCost.recurrence !== "once" && (
              <label className="block text-xs text-gray-500">Fine (opzionale)<input data-testid="cost-edit-end" type="date" className={inp} value={editCost.end_date} onChange={(e) => setEditCost({ ...editCost, end_date: e.target.value })} /></label>
            )}
            <p className="text-[11px] text-gray-400">Il nuovo importo su un costo ricorrente vale dalle occorrenze future; i periodi già trascorsi mantengono l'importo storico.</p>
          </>}
          <label className="block text-xs text-gray-500">Nota<input className={inp} value={editCost.note || ""} onChange={(e) => setEditCost({ ...editCost, note: e.target.value })} /></label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => setEditCost(null)} className="btn-outline flex-1 text-sm">Annulla</button>
            <button data-testid="cost-edit-save" onClick={saveEdit} className="btn-primary flex-1 text-sm">Salva modifiche</button>
          </div>
        </div>}
      </Modal>
    </div>
  );
};

const Row = ({ label, value, sub, testid }) => (
  <div className="flex justify-between items-start gap-3" data-testid={testid}>
    <div className="min-w-0"><div className="text-sm text-gray-600">{label}</div>{sub && <div className="text-[11px] text-gray-400">{sub}</div>}</div>
    <div className="text-sm font-semibold whitespace-nowrap">{value}</div>
  </div>
);

/* ---------- GESTIONALE END ---------- */
export const SettingsTab = ({ data, act }) => {
  const [tog, setTog] = useState(null); // {field, value, label}
  const s = data.settings;
  if (!s) return <p className="text-sm text-gray-400">Caricamento…</p>;
  return (
    <div className="card p-5 space-y-4" data-testid="settings-tab">
      <label className="flex justify-between items-center"><span className="text-sm">Pagamento online (Stripe)</span>
        <input data-testid="set-online-payment" type="checkbox" checked={s.online_payment} onChange={(e) => setTog({ field: "online_payment", value: e.target.checked, label: `${e.target.checked ? "Attivare" : "Disattivare"} il pagamento online (Stripe)?` })} /></label>
      <label className="flex justify-between items-center"><span className="text-sm">Promo "Prova MAS" attiva</span>
        <input type="checkbox" checked={s.promo_prova_mas} onChange={(e) => setTog({ field: "promo_prova_mas", value: e.target.checked, label: `${e.target.checked ? "Attivare" : "Disattivare"} la Promo "Prova MAS"?` })} /></label>
      <Modal open={!!tog} onClose={() => setTog(null)} title="Confermare la modifica?">
        <p className="text-sm text-gray-600 mb-3">{tog?.label}</p>
        <div className="flex gap-2">
          <button onClick={() => setTog(null)} className="btn-outline flex-1 text-sm">Annulla</button>
          <button data-testid="settings-toggle-confirm" onClick={() => { const t = tog; setTog(null); act(() => api.put("/admin/settings", { [t.field]: t.value }), "Impostazioni aggiornate"); }} className="btn-primary flex-1 text-sm">Conferma</button>
        </div>
      </Modal>
      <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">Prezzo Prova MAS: {fmtEur(s.promo_prova_price)} · Ore minime Vetrina: {s.vetrina_min_hours}</div>
      <div className="text-xs text-gray-400">Premi referral: Buono Amazon 100 € (Trico) · 20 € (Coworking) — consegna manuale MAS.</div>
      <div className="pt-2 border-t border-gray-100">
        {s.stripe_mode === "LIVE"
          ? <span data-testid="stripe-mode-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1"><span className="w-2 h-2 rounded-full bg-red-600" />Stripe LIVE — pagamenti reali</span>
          : <span data-testid="stripe-mode-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Stripe TEST / SANDBOX</span>}
      </div>
    </div>
  );
};

/* ---------- AUDIT LOG ---------- */
const AUDIT_LABELS = {
  course_create: "Corso creato", course_update: "Corso aggiornato", course_delete: "Corso eliminato", course_refund: "Rimborso corso",
  artist_approve: "Artista approvato", artist_suspend: "Artista sospeso", artist_reject: "Artista rifiutato",
  flash_approve: "Flash Day approvato", flash_reject: "Flash Day rifiutato", "flash_request-changes": "Modifiche richieste al Flash Day",
  led_approve: "Messaggio LED Wall approvato",
  vetrina_approve: "Messaggio Vetrina approvato", vetrina_schedule: "Messaggio Vetrina programmato",
  vetrina_transmit: "Messaggio Vetrina trasmesso", vetrina_reject: "Messaggio Vetrina rifiutato",
  update_settings: "Impostazioni aggiornate", block_availability: "Disponibilità bloccata", unblock_availability: "Disponibilità ripristinata",
  retry_fulfillment: "Riprova completamento pagamento", deliver_voucher: "Buono premio consegnato",
  lead_status: "Stato lead aggiornato", credit_adjust: "Riaccredito giornate",
  manual_payment: "Pagamento manuale / Contanti",
  confirm_day_completed: "Giornata coworking confermata", extend_package: "Scadenza pacchetto prorogata",
  change_password: "Password modificata",
  cost_create: "Costo gestionale aggiunto", cost_delete: "Costo gestionale eliminato",
  cost_update: "Costo gestionale modificato", cost_end: "Costo gestionale terminato",
  admin_booking_create: "Prenotazione creata dall'Admin",
};
const AUDIT_CAT = {
  Pagamenti: ["retry_fulfillment"],
  Corsi: ["course_create", "course_update", "course_delete", "course_refund"],
  Coworking: ["block_availability", "unblock_availability", "credit_adjust", "confirm_day_completed", "extend_package", "admin_booking_create"],
  Gestionale: ["cost_create", "cost_delete", "cost_update", "cost_end"],
  Artisti: ["artist_approve", "artist_suspend", "artist_reject"],
  "Flash Day": ["flash_approve", "flash_reject", "flash_request-changes"],
  Vetrina: ["vetrina_approve", "vetrina_schedule", "vetrina_transmit", "vetrina_reject", "led_approve"],
  Referral: ["deliver_voucher"],
  Tricopigmentazione: ["lead_status"],
  "Sicurezza / Sistema": ["update_settings", "change_password"],
};
const auditHuman = (a) => AUDIT_LABELS[a.action] || a.action;
const auditMeta = (a) => {
  const m = a.metadata || {};
  if (a.action === "course_refund") return `Rimborso ${m.amount != null ? fmtEur(m.amount) : "—"}${m.pct != null ? ` · ${m.pct}%` : ""}${m.full ? " · integrale MAS" : ""}`;
  if (a.action === "course_create") return m.title ? `Corso: ${m.title}` : "";
  if (a.action === "credit_adjust") return `${m.station_type || ""} ${m.quantity != null ? `· ${m.quantity} giornate` : ""}`.trim();
  if (a.action === "lead_status") return `${m.old_status || "?"} → ${m.new_status || "?"}`;
  if (a.action === "block_availability" || a.action === "unblock_availability") return m.scope ? `Ambito: ${m.scope}${m.days ? ` · ${m.days} gg` : ""}` : "";
  return "";
};

export const AuditTab = ({ data }) => {
  const [cat, setCat] = useState("Tutti");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(30);
  const [openTech, setOpenTech] = useState(null);
  const all = data.audit || [];
  const cats = ["Tutti", ...Object.keys(AUDIT_CAT)];
  const inCat = (a) => cat === "Tutti" || (AUDIT_CAT[cat] || []).includes(a.action);
  const inQ = (a) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [auditHuman(a), a.action, a.admin_name, a.object_id, JSON.stringify(a.metadata || {})]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
  };
  const filtered = all.filter((a) => inCat(a) && inQ(a));
  const shown = filtered.slice(0, limit);
  return (
    <div data-testid="audit-tab">
      <input data-testid="audit-search" value={q} onChange={(e) => { setQ(e.target.value); setLimit(30); }} placeholder="Cerca nel log…"
        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 mb-3" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {cats.map((k) => (
          <button key={k} data-testid={`audit-cat-${k}`} onClick={() => { setCat(k); setLimit(30); }}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${cat === k ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{k}</button>
        ))}
      </div>
      <div className="space-y-2">
        {shown.map((a) => {
          const meta = auditMeta(a);
          return (
            <div key={a.id} className="card p-3" data-testid={`audit-${a.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm">{auditHuman(a)}</div>
                  {meta && <div className="text-xs text-gray-600 mt-0.5">{meta}</div>}
                  <div className="text-[11px] text-gray-400 mt-0.5">Operatore: {a.admin_name || a.admin_id || "—"}</div>
                </div>
                <div className="text-[10px] text-gray-400 shrink-0 text-right">{fmtDateTime(a.ts)}</div>
              </div>
              <button data-testid={`audit-tech-${a.id}`} onClick={() => setOpenTech(openTech === a.id ? null : a.id)} className="text-[11px] text-violet-600 mt-1.5">
                {openTech === a.id ? "Nascondi dettagli tecnici" : "Dettagli tecnici"}
              </button>
              {openTech === a.id && (
                <div className="mt-1.5 border-t border-gray-100 pt-1.5 text-[11px] text-gray-500 space-y-0.5 break-all">
                  <div>action: <span className="text-gray-800">{a.action}</span></div>
                  {a.object_type && <div>object_type: <span className="text-gray-800">{a.object_type}</span></div>}
                  {(a.object_id || a.target) && <div>object_id: <span className="text-gray-800">{a.object_id || a.target}</span></div>}
                  {a.metadata && Object.keys(a.metadata).length > 0 && <div>payload: <span className="text-gray-800">{JSON.stringify(a.metadata)}</span></div>}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-sm text-gray-400">Nessuna operazione registrata.</p>}
      </div>
      {filtered.length > shown.length && (
        <button data-testid="audit-more" onClick={() => setLimit(limit + 30)} className="btn-outline w-full text-sm mt-3">Carica altri ({filtered.length - shown.length})</button>
      )}
    </div>
  );
};
